import type {
  AdoptServiceInput,
  ApiClient,
  ChangePasswordInput,
  ComposeImportResult,
  CorpusBindingInput,
  CreateAgentSessionInput,
  CreateAlertRuleInput,
  CreateConnectionInput,
  CreateDomainInput,
  CreateEnrollTokenInput,
  CreateEnvironmentInput,
  CreateInstanceInput,
  CreatePlacementInput,
  CreateProjectInput,
  CreateLlmCredentialInput,
  CreateProviderCredentialInput,
  CreateSecretInput,
  CreateServiceInput,
  CreateUserInput,
  EnrollToken,
  ExecStreamHandle,
  ListAgentSessionsParams,
  ListAlertInstancesParams,
  ListAlertRulesParams,
  ListAuditParams,
  ListConnectionsParams,
  ListContainersParams,
  ListDeploymentsParams,
  ListHealthParams,
  ListHostsParams,
  ListInstancesParams,
  ListLlmCredentialsParams,
  ListNodePlacementsParams,
  ListNodesParams,
  ListPlacementsParams,
  ListProjectsParams,
  ListProviderCredentialsParams,
  ListSecretsParams,
  ListServicesParams,
  LoginInput,
  RegisterInput,
  LogStreamHandle,
  LogStreamSource,
  OpenExecStreamParams,
  OpenLogStreamParams,
  Page,
  PageParams,
  PromoteServiceInput,
  PromoteSessionInput,
  PromoteSessionResult,
  ProposeImportParams,
  ProviderCatalog,
  RedeployOptions,
  RotateLlmCredentialKeyInput,
  RotateProviderCredentialInput,
  RunSessionTaskInput,
  Session,
  SetNodeEnvironmentInput,
  SSOConfigInput,
  SSOInvite,
  SSOInviteInput,
  SSOTestConnectionInput,
  SSOTestConnectionResult,
  UpdateAlertRuleInput,
  UpdateEnvironmentInput,
  UpdateLlmCredentialConfigInput,
  UpdateNodeInput,
  UpdatePlacementInput,
  UpdateProjectInput,
  UpdateServiceInput,
  UpdateUserInput,
  GetConnectionStatusesParams,
} from "./client";
import { ApiError } from "./errors";
import { createExecStreamEmitter } from "./execStream";
import {
  ADOPT_SPECS,
  ALERT_INSTANCES,
  ALERT_RULES,
  buildAuditEvents,
  buildGovernanceDecisionEvents,
  CONNECTIONS,
  CONTAINERS,
  CORPUS_ARTIFACTS,
  CORPUS_BINDINGS,
  GOVERNANCE_FINDINGS,
  GOVERNANCE_POLICIES,
  CORPUS_SYNCED_AT,
  DEPLOYMENTS,
  DEPLOYMENT_PLACEMENTS,
  DOMAINS,
  EMPTY_ADOPT_SPEC,
  ENVIRONMENTS,
  IMPORT_PROPOSAL_HETZNER,
  INSTANCES,
  AGENT_SESSIONS,
  LLM_CREDENTIALS,
  NODES,
  PLACEMENTS,
  PROJECTS,
  PROVIDER_CREDENTIALS,
  SECRETS,
  SERVICES,
  USERS,
} from "./fixtures";
import { createLogStreamEmitter } from "./logStream";
import type {
  Account,
  AdoptPreview,
  AgentSession,
  AlertCondition,
  AlertInstance,
  AlertRule,
  AuditEvent,
  AuditVerification,
  Connection,
  ConnectionStatus,
  Container,
  ContainerEvent,
  CorpusArtifact,
  CorpusBinding,
  GovernanceFinding,
  GovernancePolicy,
  CorpusList,
  Deployment,
  DeploymentDetail,
  DeploymentPlacementOutcome,
  DeployPreview,
  Domain,
  EntitlementStatus,
  Environment,
  EnvironmentClass,
  Node,
  Host,
  HostLiveAgentFacet,
  HostState,
  HealthCell,
  HealthStatus,
  ApplyImportResult,
  ImportProposal,
  Instance,
  LlmCredentialSummary,
  Placement,
  Project,
  ProposedImportItem,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  SSOConfig,
  Subscription,
  User,
} from "./types";

const DEFAULT_PAGE_LIMIT = 50;
const MAX_PAGE_LIMIT = 200;

// The mock is single-tenant after ADR-0035 M1 PR-D removed the fleet vocabulary:
// the real server derives the tenant from the session, so there is no tenant/fleet
// selector anywhere. This constant is the one implicit tenant every fixture belongs
// to (it matches the `tenant` the seed data carries), used only where the mock still
// needs a channel key or a value for the response's `tenant` field.
const MOCK_TENANT = "fleet-prod";

// Mirrors provisioning.deferredImportReasons verbatim (server/internal/provisioning/
// import.go) so the mock's "coming soon" copy matches production exactly, never a
// friendlier invented message (S-15).
const DEFERRED_IMPORT_REASONS: Record<string, string> = {
  github: "GitHub org import depends on the ADR-0044 GitHub App installation-token mint, which is not in the tree yet",
  docker:
    "Docker org import depends on a Docker Hub/registry list client and label projection onto Docker objects, neither in the tree yet",
};

// Mirrors orgmap.ProposedItem.Committable exactly (server/internal/orgmap/import.go):
// only a project-level item with high/medium confidence and no foreign-tenant conflict
// is safe to create without a human resolving it.
function isImportItemCommittable(it: ProposedImportItem): boolean {
  if (it.level !== "project") return false;
  if (it.confidence === "low") return false;
  if (it.conflicts?.some((c) => c.kind === "foreign-tenant")) return false;
  return Boolean(it.project);
}

/** Mirrors `alertsvc.AllConditions()` — the closed, validated set a rule's
 * `condition` must be one of (O-6). */
const VALID_ALERT_CONDITIONS: AlertCondition[] = [
  "placement_failed",
  "node_offline",
  "connection_unreachable",
  "cert_expiry",
];

/** Mirrors `store.AllEnvironmentClasses` (ADR-0035 §1) — the closed set an
 * environment's `class` must be one of. */
const VALID_ENVIRONMENT_CLASSES: EnvironmentClass[] = ["development", "preview", "staging", "production"];

/** Mirrors `store.NodeTypeStandard`/`store.NodeTypeLocal` — the closed set an
 * environment's `allowedNodeTypes` entries must each be one of. */
const VALID_NODE_TYPES = ["standard", "local"];

/** Allow-lists an environment's node-type restriction (P-6/S-19), mirroring
 * `environmentsvc.validateAllowedNodeTypes`: empty is "no restriction", every
 * entry must be a known node type. Throws an `ApiError` (400) shaped like the
 * server's refusal for an unknown type. */
function validateEnvironmentNodeTypes(types: string[]): string[] {
  const out: string[] = [];
  for (const t of types) {
    if (!VALID_NODE_TYPES.includes(t)) {
      throw new ApiError(`Allowed node type "${t}" is not one of ${VALID_NODE_TYPES.join(", ")}.`, 400, "invalid_environment");
    }
    out.push(t);
  }
  return out;
}

/** Mirrors `proto.ProvenanceRung`'s closed set (`repo-watch` | `ci-attested` |
 * `signed-commit`) — the same set `corpusbindingsvc.validatePolicy` requires
 * `expectedRung` to be one of (an unknown rung could never match a proven bundle,
 * so it would be an inert badge, A-10). */
const VALID_PROVENANCE_RUNGS = ["repo-watch", "ci-attested", "signed-commit"];

/** Trims and range-checks a corpus binding policy, mirroring
 * `corpusbindingsvc.validatePolicy`'s rules closely enough to exercise the same refusals
 * client-side tests rely on: an empty `repoRef`/`corpusPath`, an `expectedRung` outside the
 * known set, or an `allowedRefs` list that ends up empty after trimming (which would author
 * a binding that authorizes NO handoff at all — A-10) all throw a 400 `ApiError`. Returns a
 * cleaned copy (trimmed strings, de-duplicated refs) so the caller stores what was actually
 * validated, not the raw input. */
function validateCorpusBindingInput(input: CorpusBindingInput): CorpusBindingInput {
  const repoRef = input.repoRef.trim();
  if (!repoRef) {
    throw new ApiError("Repository is required.", 400, "invalid_corpus_binding");
  }
  const corpusPath = input.corpusPath.trim();
  if (!corpusPath) {
    throw new ApiError("Corpus path is required.", 400, "invalid_corpus_binding");
  }
  const expectedRung = input.expectedRung.trim();
  if (!VALID_PROVENANCE_RUNGS.includes(expectedRung)) {
    throw new ApiError(
      `Expected rung must be one of ${VALID_PROVENANCE_RUNGS.join(", ")}.`,
      400,
      "invalid_corpus_binding",
    );
  }
  const expectedIssuer = input.expectedIssuer.trim();
  const allowedRefs = Array.from(new Set(input.allowedRefs.map((r) => r.trim()).filter(Boolean)));
  if (allowedRefs.length === 0) {
    throw new ApiError(
      "At least one allowed ref is required — an empty list would authorize no sync.",
      400,
      "invalid_corpus_binding",
    );
  }
  return { repoRef, corpusPath, expectedRung, expectedIssuer, allowedRefs };
}

/** A believable snapshot of Hetzner's live catalog (charter P-1: the real
 * client hits the provider through the server; this stands in for that so
 * the provisioning modal exercises the same shape in the demo). Prices are
 * illustrative EUR/mo figures, not guaranteed current. */
const MOCK_PROVIDER_CATALOG: ProviderCatalog = {
  locations: [
    { name: "fsn1", description: "Falkenstein DC Park 1", city: "Falkenstein", country: "DE" },
    { name: "nbg1", description: "Nuremberg DC Park 1", city: "Nuremberg", country: "DE" },
    { name: "hel1", description: "Helsinki DC Park 1", city: "Helsinki", country: "FI" },
    { name: "ash", description: "Ashburn, VA", city: "Ashburn", country: "US" },
  ],
  serverTypes: [
    {
      name: "cx23",
      description: "Shared vCPU, Intel/AMD",
      cores: 2,
      memoryGB: 4,
      diskGB: 40,
      storageType: "local",
      cpuType: "shared",
      prices: [
        { location: "fsn1", priceMonthlyGross: "3.79" },
        { location: "nbg1", priceMonthlyGross: "3.79" },
        { location: "hel1", priceMonthlyGross: "3.79" },
        { location: "ash", priceMonthlyGross: "4.59" },
      ],
    },
    {
      name: "cx33",
      description: "Shared vCPU, Intel/AMD",
      cores: 4,
      memoryGB: 8,
      diskGB: 80,
      storageType: "local",
      cpuType: "shared",
      prices: [
        { location: "fsn1", priceMonthlyGross: "6.90" },
        { location: "nbg1", priceMonthlyGross: "6.90" },
        { location: "hel1", priceMonthlyGross: "6.90" },
        { location: "ash", priceMonthlyGross: "8.39" },
      ],
    },
    {
      name: "cx43",
      description: "Shared vCPU, Intel/AMD",
      cores: 8,
      memoryGB: 16,
      diskGB: 160,
      storageType: "local",
      cpuType: "shared",
      prices: [
        { location: "fsn1", priceMonthlyGross: "13.10" },
        { location: "nbg1", priceMonthlyGross: "13.10" },
        { location: "hel1", priceMonthlyGross: "13.10" },
        { location: "ash", priceMonthlyGross: "15.99" },
      ],
    },
    {
      name: "cpx11",
      description: "Dedicated vCPU, AMD",
      cores: 2,
      memoryGB: 2,
      diskGB: 40,
      storageType: "local",
      cpuType: "dedicated",
      prices: [
        { location: "fsn1", priceMonthlyGross: "4.49" },
        { location: "nbg1", priceMonthlyGross: "4.49" },
        { location: "hel1", priceMonthlyGross: "4.49" },
      ],
    },
  ],
};

/** A believable rotation of stdout/stderr lines for the simulated live-tail
 * (`openLogStream` below) — enough variety to make stderr's visual
 * distinction and the auto-scroll/bounded-buffer behavior obvious in a demo,
 * without pretending to be any particular real workload's output. */
const MOCK_LOG_LINES: ReadonlyArray<{ stream: LogStreamSource; message: string }> = [
  { stream: "stdout", message: "Listening on :8080" },
  { stream: "stdout", message: "Connected to database" },
  { stream: "stdout", message: "GET /healthz 200 1ms" },
  { stream: "stdout", message: "GET /api/v1/status 200 4ms" },
  { stream: "stderr", message: "WARN: slow query (312ms) on containers table" },
  { stream: "stdout", message: "Cache warm: 1284 keys loaded" },
  { stream: "stderr", message: "ERROR: upstream timeout, retrying (1/3)" },
  { stream: "stdout", message: "Retry succeeded" },
  { stream: "stdout", message: "GET /api/v1/containers 200 9ms" },
  { stream: "stdout", message: "Heartbeat ok" },
];

/** A handful of recognized commands for the mock interactive shell
 * (`openExecStream` below) — enough to demo a real-feeling session (listing,
 * identity, environment) without pretending to be a full shell. Anything
 * else falls back to a "command not found" line, same as a real one would. */
const MOCK_SHELL_RESPONSES: Readonly<Record<string, string>> = {
  ls: "bin  etc  lib  proc  usr  var",
  pwd: "/",
  whoami: "root",
  id: "uid=0(root) gid=0(root) groups=0(root)",
  hostname: "container",
  uname: "Linux",
  "uname -a": "Linux container 6.1.0 #1 SMP x86_64 GNU/Linux",
  env: "PATH=/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin\nHOME=/root",
  date: new Date().toUTCString(),
  echo: "",
};

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Cursor is just an opaque stringified offset — callers must not parse it,
 * matching how a real server would hand back an opaque token. */
function paginate<T>(items: T[], params: PageParams): Page<T> {
  const limit = Math.min(params.limit ?? DEFAULT_PAGE_LIMIT, MAX_PAGE_LIMIT);
  const offset = params.cursor ? Number.parseInt(params.cursor, 10) : 0;
  const start = Number.isFinite(offset) && offset > 0 ? offset : 0;
  const page = items.slice(start, start + limit);
  const nextOffset = start + limit;
  const nextCursor = nextOffset < items.length ? String(nextOffset) : null;
  return { items: page, nextCursor };
}

/** Mirrors the server's `imageTag` exactly (server/internal/projectsvc/http.go,
 * Host/Deploy UX Phase 2, #539) — the mock's own stand-in for the same
 * derivation, so a deployment's `tag` here matches what the real server would
 * compute for the same `image`. Returns "" for a digest-pinned reference (one
 * containing '@') or an image with no tag, rather than inventing "latest"
 * (A-10) — display-only, never the authoritative reference. */
function imageTag(image: string): string {
  const trimmed = image.trim();
  if (!trimmed || trimmed.includes("@")) return "";
  const lastSlash = trimmed.lastIndexOf("/");
  const colon = trimmed.lastIndexOf(":");
  return colon > lastSlash ? trimmed.slice(colon + 1) : "";
}

// ---- Compose import (ADR-0025 §2) — mock-side parse ----
// A deliberately minimal, indentation-driven subset of compose.yml: enough to
// exercise the demo/tests deterministically (service name + `image:` +
// `depends_on:`, both list and inline-array forms), NOT a general YAML parser.
// The real server's decomposition (server/internal/composeio) is the actual
// contract; this only has to behave believably for the mock adapter. Any
// nested key this parser doesn't recognize is skipped rather than rejected —
// matching "minimal subset" scope, not full compose fidelity.

interface MockComposeService {
  name: string;
  image?: string;
  dependsOn: string[];
}

function stripComposeQuotes(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && (trimmed[0] === '"' || trimmed[0] === "'") && trimmed.endsWith(trimmed[0])) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function parseInlineArray(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return [];
  const inner = trimmed.slice(1, -1).trim();
  if (!inner) return [];
  return inner.split(",").map((item) => stripComposeQuotes(item.trim())).filter(Boolean);
}

/** Parses `composeText` into the service/depends_on shape this mock needs.
 * Throws an `ApiError` (400) shaped like the real server's itemized,
 * field-naming parse errors (never echoing arbitrary file content, S-13) for
 * anything outside the supported subset. */
function parseComposeForMock(composeText: string): MockComposeService[] {
  const rawLines = composeText.split(/\r?\n/);
  const lines = rawLines.map((line) => {
    // A leading '#' line is a full-line comment; a trailing " # ..." on an
    // otherwise-content line is intentionally NOT stripped here (keeps this
    // parser simple) — real compose files rarely inline-comment scalar
    // values in the shapes this mock cares about (image/depends_on).
    const indent = line.length - line.trimStart().length;
    return { indent, trimmed: line.trim() };
  });

  const servicesIdx = lines.findIndex((l) => /^services\s*:\s*$/.test(l.trimmed));
  if (servicesIdx < 0) {
    throw new ApiError("compose: 'services' is required and must declare at least one service", 400, "invalid_compose");
  }

  let i = servicesIdx + 1;
  while (i < lines.length && (lines[i].trimmed === "" || lines[i].trimmed.startsWith("#"))) i++;
  if (i >= lines.length) {
    throw new ApiError("compose: 'services' must declare at least one service", 400, "invalid_compose");
  }

  const serviceIndent = lines[i].indent;
  const services: MockComposeService[] = [];

  while (i < lines.length) {
    const line = lines[i];
    if (line.trimmed === "" || line.trimmed.startsWith("#")) {
      i++;
      continue;
    }
    if (line.indent < serviceIndent) break; // left the services block entirely
    if (line.indent > serviceIndent) {
      // Stray over-indented line at the top of the block — skip defensively
      // rather than crash the demo.
      i++;
      continue;
    }

    const nameMatch = /^([A-Za-z0-9][A-Za-z0-9_.-]*)\s*:\s*$/.exec(line.trimmed);
    if (!nameMatch) {
      throw new ApiError(`compose: invalid service definition near "${line.trimmed}"`, 400, "invalid_compose");
    }
    const name = nameMatch[1];
    i++;

    let image: string | undefined;
    const dependsOn: string[] = [];

    while (i < lines.length && (lines[i].trimmed === "" || lines[i].indent > serviceIndent)) {
      const inner = lines[i];
      if (inner.trimmed === "" || inner.trimmed.startsWith("#")) {
        i++;
        continue;
      }
      const keyMatch = /^([A-Za-z0-9_]+)\s*:\s*(.*)$/.exec(inner.trimmed);
      if (!keyMatch) {
        i++;
        continue;
      }
      const key = keyMatch[1];
      const rest = keyMatch[2].trim();
      const keyIndent = inner.indent;
      i++;

      if (key === "image") {
        image = stripComposeQuotes(rest);
        continue;
      }
      if (key === "depends_on") {
        if (rest) {
          dependsOn.push(...parseInlineArray(rest));
          continue;
        }
        while (i < lines.length && lines[i].trimmed.startsWith("- ")) {
          dependsOn.push(stripComposeQuotes(lines[i].trimmed.slice(2)));
          i++;
        }
        continue;
      }
      // Unrecognized key for this mock's subset (ports/networks/environment/
      // etc.): skip its own indent-bounded nested block without failing.
      while (i < lines.length && (lines[i].trimmed === "" || lines[i].indent > keyIndent)) i++;
    }

    services.push({ name, image, dependsOn });
  }

  if (services.length === 0) {
    throw new ApiError("compose: 'services' must declare at least one service", 400, "invalid_compose");
  }
  return services;
}

type EventHandler = (event: ContainerEvent) => void;

export interface MockApiClientOptions {
  /** Overrides the simulated per-call network latency. Omitted (the
   * default) keeps the believable random 220-400ms-ish delay every call
   * for dev-server/demo use — EXCEPT under Vitest (`import.meta.env.MODE ===
   * "test"`), where an omitted delay defaults to `0` so tests are deterministic
   * and hermetic (charter Q-5): parallel fetches resolve in a fixed order and
   * nothing waits on a randomized `setTimeout` (#355/#315). `0` skips
   * `setTimeout` entirely and resolves on the next microtask — so a
   * `screen.findBy...` doesn't burn real wall-clock time waiting on fixtures
   * that were never going to change (P-6 doesn't apply to a mock's own timing,
   * only to what it's simulating). Any other positive number replaces every
   * call's delay with that fixed value — useful for a test that DELIBERATELY
   * wants to observe a loading state. */
  delay?: number;
  /**
   * Overrides the simulated timing of `createInstance`'s background
   * requested -> provisioning -> running -> agent-enrolled arc (ms elapsed
   * since creation at which each stage takes effect). Defaults to a
   * believable dev/demo pace (see `DEFAULT_PROVISION_STAGE_DELAYS_MS`). A
   * test that wants to observe the WHOLE Host-state sequence (Host/Deploy UX
   * Phase 3 — provisioning-as-progress) without burning real wall-clock time
   * passes small values here; any stage left unset keeps its default.
   */
  provisionStageDelaysMs?: Partial<{ provisioning: number; running: number; enrolled: number }>;
  /**
   * Overrides the simulated delay before a `nodeType: "local"` enroll token
   * (minted via `createEnrollToken`) "connects" as an online `local` node —
   * standing in for an operator actually running the printed
   * `kelpie -local` command on their laptop (the deploy wizard's "use this
   * laptop" step, journey.md rung 3/G13). Defaults to a believable dev/demo
   * pace; a test that wants to observe the wait state passes a small value.
   */
  localEnrollDelayMs?: number;
}

/** Realistic dev/demo pacing for `createInstance`'s simulated arc — real
 * provisioning takes tens of seconds and a real Kelpie install takes longer
 * still, but this stays quick enough that a developer clicking "Provision"
 * sees the whole requested -> provisioning -> enrolling -> online story play
 * out during a demo rather than a long, dead wait. */
const DEFAULT_PROVISION_STAGE_DELAYS_MS = { provisioning: 600, running: 2200, enrolled: 3800 };

/**
 * In-memory implementation of `ApiClient`. Stands in for the control-plane
 * API during frontend development; every method's latency, pagination, and
 * error behavior is meant to mirror what the real server should do so
 * swapping this out later is a one-file change (see `ApiProvider.tsx`).
 */
export function createMockApiClient(options: MockApiClientOptions = {}): ApiClient {
  // Under Vitest, `import.meta.env.MODE` is "test" (dev is "development",
  // release builds "production"), so every simulated latency collapses to 0:
  // fetch ordering becomes deterministic and no test burns real wall-clock or
  // races two parallel `useQuery` fetches against a randomized `setTimeout`
  // (charter Q-5 — deterministic/hermetic, no wall-clock; #355/#315). An
  // explicit `options.delay` still wins, so a test that WANTS to exercise a
  // loading state can opt back into a delay. Outside tests the believable
  // random jitter below is untouched, keeping dev/demo loading states honest.
  const configuredDelayMs = options.delay ?? (import.meta.env.MODE === "test" ? 0 : undefined);

  // Unlike `configuredDelayMs`, this does NOT collapse to 0 under Vitest —
  // these timers simulate a real BACKGROUND process (the provider building a
  // machine, Kelpie's first check-in), not per-call request latency, so a
  // test that wants to observe the arc opts into short values explicitly via
  // `options.provisionStageDelaysMs` (see its doc comment) rather than this
  // silently going instant the way per-call `delay()` does.
  const provisionStageDelaysMs = { ...DEFAULT_PROVISION_STAGE_DELAYS_MS, ...options.provisionStageDelaysMs };
  const localEnrollDelayMs = options.localEnrollDelayMs ?? 2600;

  /** Simulated network latency so loading states are visible and real —
   * not so slow that the app feels sluggish in dev. `configuredDelayMs`
   * (above) overrides the per-call `ms` argument when this mock was
   * constructed with an explicit `delay` option, or whenever it runs under
   * test (where `configuredDelayMs` defaults to 0 — see above). */
  function delay<T>(value: T, ms = 220 + Math.round(Math.random() * 180)): Promise<T> {
    const effectiveMs = configuredDelayMs ?? ms;
    if (effectiveMs === 0) {
      return Promise.resolve(value);
    }
    return new Promise((resolve) => setTimeout(() => resolve(value), effectiveMs));
  }

  // Mutable working copies — fixtures.ts stays the pristine seed.
  const containers = new Map<string, Container>(clone(CONTAINERS).map((c) => [c.id, c]));
  const secrets = new Map<string, SecretSummary>(clone(SECRETS).map((s) => [s.id, s]));
  const users = new Map<string, User>(clone(USERS).map((u) => [u.id, u]));
  // Mutable working copy of NODES — everything else in this file that only
  // ever READS node fleet/id membership (entitlement counts below) still
  // reads the pristine NODES array directly; only the two ops that can
  // observe a write (`requireNode`, `listNodes`) go through this map, same
  // split as every other resource above.
  const nodes = new Map<string, Node>(clone(NODES).map((n) => [n.id, n]));
  // ---- Hero flow working copies ----
  const projects = new Map<string, Project>(clone(PROJECTS).map((p) => [p.id, p]));
  // ---- Corpus (ADR-0052 read / ADR-0053 §3 binding) working copies ----
  // Keyed by projectId, mirroring how the real routes are scoped (no separate id — one
  // binding per project, one artifact list per project). An id with no entry in
  // corpusArtifacts reads as "nothing applied yet" (sourceCommit ""); an id with no entry
  // in corpusBindings reads as "not bound" (404) — both are honest absence, never fabricated.
  const corpusArtifacts = new Map<string, CorpusArtifact[]>(Object.entries(clone(CORPUS_ARTIFACTS)));
  const corpusBindings = new Map<string, CorpusBinding>(Object.entries(clone(CORPUS_BINDINGS)));
  const corpusSyncedAt = new Map<string, string>(Object.entries(clone(CORPUS_SYNCED_AT)));
  // Governing-agent findings (ADR-0059), keyed by finding id (each carries its own projectId). A
  // finding is APPENDED only by the server-side producer in production — the mock pre-seeds them and
  // exposes only list + resolve, never a create, mirroring the human-only client surface.
  const governanceFindings = new Map<string, GovernanceFinding>(
    Object.values(clone(GOVERNANCE_FINDINGS)).flat().map((f) => [f.id, f]),
  );
  const services = new Map<string, Service>(clone(SERVICES).map((s) => [s.id, s]));
  const deployments = new Map<string, Deployment>(clone(DEPLOYMENTS).map((d) => [d.id, d]));
  // Per-deployment convergence breakdown (Host/Deploy UX Phase 2, #539), keyed by
  // deployment id — the mock's twin of the server's stored per-placement outcome
  // summary. Deliberately a SEPARATE map from `deployments`, mirroring the real
  // wire contract: `Deployment` (the list shape) never carries `placements`, only
  // `DeploymentDetail` (`getDeployment`) does. An id with no entry here means "no
  // outcome summary yet" (an in-flight deploy), read back as an empty array —
  // never fabricated (A-10), see `getDeployment` below.
  const deploymentPlacements = new Map<string, DeploymentPlacementOutcome[]>(
    Object.entries(clone(DEPLOYMENT_PLACEMENTS)),
  );
  const providerCredentials = new Map<string, ProviderCredentialSummary>(
    clone(PROVIDER_CREDENTIALS).map((c) => [c.id, c]),
  );
  const llmCredentials = new Map<string, LlmCredentialSummary>(
    clone(LLM_CREDENTIALS).map((c) => [c.id, c]),
  );
  const agentSessions = new Map<string, AgentSession>(
    clone(AGENT_SESSIONS).map((s) => [s.id, s]),
  );
  const instances = new Map<string, Instance>(clone(INSTANCES).map((i) => [i.id, i]));
  // ---- Fleet-health alerting (ADR-0030 P4-5) working copies ----
  const alertRules = new Map<string, AlertRule>(clone(ALERT_RULES).map((r) => [r.id, r]));
  const alertInstances = new Map<string, AlertInstance[]>(
    Object.entries(clone(ALERT_INSTANCES)),
  );
  // `AlertRule` (the wire shape) carries no fleet/tenant field — the real
  // server scopes alert rules by the {fleet} URL segment only, never a
  // response field (mirrors RuleJSON exactly, see types.ts). This mock
  // needs its own bookkeeping to enforce that same scoping locally.
  const alertRuleFleet = new Map<string, string>(ALERT_RULES.map((r) => [r.id, "fleet-prod"]));
  // ---- The Placement Graph (ADR-0025) working copies ----
  const placements = new Map<string, Placement>(clone(PLACEMENTS).map((p) => [p.id, p]));
  const connections = new Map<string, Connection>(clone(CONNECTIONS).map((c) => [c.id, c]));
  // ---- Adopt bind bookkeeping (ADR-0038 P3/P4b) ----
  // Mirrors the server's `(tenant, container_id)` partial-unique index (migration 0064,
  // #324) that refuses a second adopt of the SAME container — this mock has no database,
  // so it tracks the constraint itself. Deliberately NOT modeled by flipping
  // `Container.managed` on adopt: adoption never relabels the container (ADR-0038 §3), so
  // the real `managed` flag (label-derived) stays false even once adopted, and this mock
  // mirrors that honestly rather than fabricating a label flip for a nicer demo. A real
  // client never needs this bookkeeping itself — the server enforces the constraint
  // regardless of what the browser tracks; it exists here purely to simulate that
  // server-side behavior without a real backend.
  const adoptedContainerIds = new Set<string>();
  const serviceAdoptedContainer = new Map<string, string>();
  // ---- Service domain bindings (ADR-0026 §3/§5) working copy ----
  const domains = new Map<string, Domain>(clone(DOMAINS).map((d) => [d.id, d]));
  // ---- First-class environments (ADR-0035 §1, M1 PR-E) working copy ----
  // Tenant-scoped, not fleet-scoped: this mock is single-tenant, so the list
  // returns every environment regardless of which fleet is selected above.
  const environments = new Map<string, Environment>(clone(ENVIRONMENTS).map((e) => [e.id, e]));
  // Lazily built per fleet on first read, then held stable for the session —
  // mirrors a real append-only trail staying put once generated, and keeps
  // this from re-randomizing (it isn't random, but re-deriving) on every call.
  const auditEventsByFleet = new Map<string, AuditEvent[]>();
  const subscribers = new Map<string, Set<EventHandler>>();
  let secretSeq = 0;
  let userSeq = 0;
  let projectSeq = 0;
  let serviceSeq = 0;
  let deploymentSeq = 0;
  let providerCredentialSeq = 0;
  let llmCredentialSeq = 0;
  let agentSessionSeq = 0;
  let instanceSeq = 0;
  let placementSeq = 0;
  let connectionSeq = 0;
  let domainSeq = 0;
  let alertRuleSeq = 0;
  let environmentSeq = 0;
  let localNodeSeq = 0;

  // The mock's stand-in for "who's logged in" — id matches fixtures.ts's
  // seeded `usr-admin` row so the "can't act on your own account" guards
  // below have something concrete to check against. `login()` (below,
  // pre-existing behavior) always grants role "admin" for any non-empty
  // credentials so every demo can reach the admin nav; it also updates the
  // display fields here to match whatever username was typed, which is why
  // this can drift from the `usr-admin` row's own username/displayName —
  // acceptable for a mock, called out so it isn't mistaken for a bug.
  let account: Account = { id: "usr-admin", username: "admin", displayName: "Admin", role: "admin" };

  // ---- OIDC / SSO (ADR-0037 PR-2b) ----
  // Starts unconfigured, mirroring a fresh tenant's `GET /api/sso/config` (the
  // server returns an empty, disabled view — not an error — until the first
  // save). `sealedSecret` stands in for the vault: only WHETHER one is set is
  // ever exposed (secretSet), the value itself is discarded immediately below,
  // same write-only contract as every other secret in this mock (S-13).
  let ssoConfig: SSOConfig = {
    configured: false,
    issuer: "",
    clientId: "",
    enabled: false,
    secretSet: false,
    callbackUrl: `https://mock.comebye.local/t/${MOCK_TENANT}/sso/callback`,
  };
  let ssoInviteSeq = 0;
  // Tracks invited SSO emails for the "already invited" collision check only
  // (`User` deliberately carries no email field — see its doc comment; email
  // is a write/match-only concept, mirroring the real server's userView not
  // having one either). Keyed by user id so it can be looked up/cleaned up
  // alongside `users` without living ON the shared User objects.
  const ssoInviteEmailByUserId = new Map<string, string>();

  // A plausible unsubscribed tenant, so the billing page renders its real
  // states in the demo. `nodeCount` is the BILLED quantity — the one
  // fleet-derived number the billing surface exposes, so a billing_admin can
  // see what it is paying for without holding node:view.
  const subscription: Subscription = {
    tenant: MOCK_TENANT,
    tier: "free",
    status: "none",
    nodeCapacity: 5,
    subscribed: false,
    nodeCount: NODES.length,
  };

  // Deliberately AT the Free node ceiling (NODES.length === 5, matching the
  // real Free cap and its +2 grace, ADR-0017 §3/§2.3) — this is what makes
  // "approaching/at the limit" demonstrable in the demo build without a
  // server. `state: "absent"` (no license has ever been installed) is the
  // ordinary self-hosted-day-one case; paired with being at capacity it is
  // exactly the case the honesty surface exists for.
  const entitlement: EntitlementStatus = {
    tenant: "demo-tenant",
    tier: "free",
    state: "absent",
    nodeLimit: { Value: 5, Unlimited: false, Grace: 2 },
    envLimit: { Value: 3, Unlimited: false, Grace: 0 },
    features: ["rollback", "health_gate", "secrets", "rbac", "sso", "audit_log", "cve_detection"],
    message:
      "No license is installed, so the Free plan applies: 5 nodes (plus 2 grace) and 3 environments. " +
      "Install a license from Settings → Billing to raise these limits.",
    usage: {
      nodes: { used: NODES.length, limit: 5, grace: 2, unlimited: false },
    },
  };

  function emit(event: ContainerEvent) {
    for (const handler of subscribers.get(MOCK_TENANT) ?? []) {
      handler(event);
    }
  }

  function auditEventsFor(): AuditEvent[] {
    let events = auditEventsByFleet.get(MOCK_TENANT);
    if (!events) {
      // The generic trail plus the governed-promotion decisions (ADR-0058/#562) — one
      // merged, newest-first trail, mirroring a real tenant's audit_events table where
      // governance.decision rows interleave with everything else. Governance Decisions
      // (features/governance/) reads this SAME trail filtered by action, never a
      // separate mock source (A-5).
      events = [...buildAuditEvents(MOCK_TENANT), ...buildGovernanceDecisionEvents(MOCK_TENANT)].sort(
        (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      );
      auditEventsByFleet.set(MOCK_TENANT, events);
    }
    return events;
  }

  function activeAdminCount(): number {
    return Array.from(users.values()).filter((u) => u.role === "admin" && u.active).length;
  }

  // Mirrors the allow-list a real username field would enforce: short,
  // simple, no whitespace/control characters.
  function validUsername(name: string): boolean {
    return /^[a-zA-Z0-9._-]{1,64}$/.test(name);
  }

  // A container id is only unique per node (mirrors the real backend), so every
  // lookup is scoped by tenant + nodeId + containerId.
  function requireContainer(nodeId: string, containerId: string): Container {
    const container = containers.get(containerId);
    if (!container || container.nodeId !== nodeId) {
      throw new ApiError(
        `Container "${containerId}" was not found on that node in this fleet. It may have been removed.`,
        404,
        "container_not_found",
      );
    }
    return container;
  }

  function requireProject(projectId: string): Project {
    const project = projects.get(projectId);
    if (!project) {
      throw new ApiError(`Project "${projectId}" was not found in this fleet.`, 404, "not_found");
    }
    return project;
  }

  // ---- Deployment timeline helpers (Host/Deploy UX Phase 2, #539) ----

  /** The most recent RUNNING deployment's image for a service, or "" when
   * there isn't one yet — the `previousImage` a fresh rollback/redeploy row
   * would record, mirroring how the real server snapshots it at deploy time
   * rather than re-reading it later (A-11). */
  function currentRunningImage(serviceId: string): string {
    const running = Array.from(deployments.values())
      .filter((d) => d.serviceId === serviceId && d.status === "running")
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return running[0]?.image ?? "";
  }

  /** Best-effort resolution of the requires_promotion environment (if any)
   * gating a service's placements — enough fidelity for the mock's
   * requires_promotion demo on `redeployDeployment` without re-implementing
   * full ADR-0035 admission (the real gate is server-side and authoritative,
   * S-8). Returns the first requires-promotion environment among the
   * service's placed nodes, or undefined when none of them requires it. */
  function findGatingEnvironment(serviceId: string): Environment | undefined {
    const nodeIds = Array.from(placements.values())
      .filter((p) => p.serviceId === serviceId)
      .map((p) => p.nodeId);
    for (const nodeId of nodeIds) {
      const node = nodes.get(nodeId);
      const env = node?.environmentId ? environments.get(node.environmentId) : undefined;
      if (env?.requiresPromotion) return env;
    }
    return undefined;
  }

  /**
   * Validates a rollback/redeploy TARGET deployment — mirrors the server's own
   * synchronous checks (server/internal/projectsvc/deploy.go's `Rollback`/
   * `Redeploy`): it must belong to THIS service (a deployment of another
   * service is 404, never revealing whether the id exists elsewhere — S-17),
   * reached the fully-"running" terminal state (never a failed/rolled_back/
   * in-flight attempt), and recorded an image to reproduce.
   */
  function requireDeploymentTarget(service: Service, deploymentId: string, verb: "rollback" | "redeploy"): Deployment {
    const target = deployments.get(deploymentId);
    if (!target || target.serviceId !== service.id) {
      throw new ApiError(`Deployment "${deploymentId}" was not found for this service.`, 404, "not_found");
    }
    if (target.status !== "running") {
      throw new ApiError(
        `Can't ${verb} to deployment ${deploymentId} — it never reached a fully-running state (status: "${target.status}").`,
        409,
        `${verb}_target_not_running`,
      );
    }
    if (!target.image?.trim()) {
      throw new ApiError(`Can't ${verb} to deployment ${deploymentId} — it recorded no image to ${verb} to.`, 409, `${verb}_target_missing`);
    }
    return target;
  }

  /**
   * Shared "deploying -> settle" timer for `rollbackDeployment`/
   * `redeployDeployment` — both re-apply a validated TARGET deployment's
   * recorded image through the same accepted-not-awaited shape
   * `deployService` uses (see its own doc comment), so the timeline shows the
   * same live-convergence behavior for a rollback/redeploy row as it does for
   * a forward deploy. Carries the target's own `tag`/`digest` through as-is —
   * this mock has no real registry to re-resolve a tag against, so it cannot
   * honestly claim a rollback's tag moved since the target ran.
   */
  function triggerRedeployLikeAction(
    service: Service,
    target: Deployment,
    createdBy: string,
    options?: RedeployOptions,
  ): Promise<Deployment> {
    const servicePlacements = Array.from(placements.values()).filter((p) => p.serviceId === service.id);
    deploymentSeq += 1;
    const now = new Date().toISOString();
    const deployment: Deployment = {
      id: `dep-mock-${deploymentSeq}`,
      serviceId: service.id,
      tenant: MOCK_TENANT,
      status: "deploying",
      targetCount: service.desiredCount,
      convergedCount: 0,
      image: target.image,
      tag: target.tag,
      digest: target.digest,
      strategy: "rolling",
      maxUnavailable: options?.maxUnavailable ?? 1,
      haltOnly: options?.haltOnly ?? false,
      previousImage: currentRunningImage(service.id) || undefined,
      createdBy,
      createdAt: now,
      updatedAt: now,
    };
    deployments.set(deployment.id, deployment);

    for (const p of servicePlacements) {
      placements.set(p.id, { ...p, status: "deploying", updatedAt: now });
    }

    setTimeout(() => {
      const settledAt = new Date().toISOString();
      const outcomes: DeploymentPlacementOutcome[] = [];
      for (const p of servicePlacements) {
        const current = placements.get(p.id);
        if (!current || current.status !== "deploying") continue; // deleted/moved meanwhile
        placements.set(p.id, { ...current, status: "running", updatedAt: settledAt });
        outcomes.push({ placementId: p.id, nodeId: p.nodeId, status: "running", digest: target.digest });
      }
      const currentDep = deployments.get(deployment.id);
      if (currentDep && currentDep.status === "deploying") {
        deployments.set(deployment.id, {
          ...currentDep,
          status: "running",
          convergedCount: outcomes.length,
          updatedAt: settledAt,
        });
        deploymentPlacements.set(deployment.id, outcomes);
      }
      const currentSvc = services.get(service.id);
      if (currentSvc) {
        services.set(service.id, { ...currentSvc, status: "running", updatedAt: settledAt });
      }
    }, 500);

    return delay(clone(deployment), 220);
  }

  function requireService(projectId: string, serviceId: string): Service {
    const service = services.get(serviceId);
    if (!service || service.projectId !== projectId) {
      throw new ApiError(`Service "${serviceId}" was not found in that project.`, 404, "not_found");
    }
    return service;
  }

  function requireProviderCredential(id: string): ProviderCredentialSummary {
    const credential = providerCredentials.get(id);
    if (!credential) {
      throw new ApiError(`Provider credential "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return credential;
  }

  function requireLlmCredential(id: string): LlmCredentialSummary {
    const credential = llmCredentials.get(id);
    if (!credential) {
      throw new ApiError(`LLM credential "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return credential;
  }

  function requireAgentSession(id: string): AgentSession {
    const session = agentSessions.get(id);
    if (!session) {
      throw new ApiError(`Agent session "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return session;
  }

  function requireAlertRule(id: string): AlertRule {
    const rule = alertRules.get(id);
    if (!rule) {
      throw new ApiError(`Alert rule "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return rule;
  }

  /**
   * Fleet health matrix (ADR-0030 P4-2) — computed FRESH on every call from
   * the live PLACEMENTS/SERVICES/NODES working copies, never cached (charter
   * A-5: a derived view is reconstructable from source of truth, not a
   * second copy that can drift). Mirrors `projectsvc.rollupHealth`'s
   * precedence (health.go): a confirmed-offline node makes the cell
   * "unreachable" regardless of placement status; "failed" placements are
   * fatal; "pending"/"deploying" haven't converged yet; a "running"
   * placement on a connected/unconfirmed node reads as healthy. This mock
   * has no live connection-reachability signal to fold in (the real
   * server's `degradedRequiredConnections` is itself a documented stub until
   * live dial health lands — see health.go), so "unhealthy" is never
   * produced here; that is an honest gap, not a missed case.
   */
  function computeHealthCells(): HealthCell[] {
    const cells: HealthCell[] = [];
    for (const placement of placements.values()) {
      const service = services.get(placement.serviceId);
      if (!service) continue;
      const node = nodes.get(placement.nodeId);
      let health: HealthStatus;
      let detail = "";
      if (node?.status === "offline") {
        health = "unreachable";
        detail = "machine is offline";
      } else if (placement.status === "failed") {
        health = "failed";
        detail = "reported failed";
      } else if (placement.status === "pending" || placement.status === "deploying") {
        health = "pending";
        detail = "still rolling out";
      } else if (placement.status === "running") {
        health = "running";
      } else {
        health = "unknown";
        detail = "unrecognized status";
      }
      cells.push({
        placementId: placement.id,
        serviceId: placement.serviceId,
        serviceName: service.name,
        nodeId: placement.nodeId,
        health,
        detail: detail || undefined,
      });
    }
    return cells.sort(
      (a, b) => a.serviceName.localeCompare(b.serviceName) || a.nodeId.localeCompare(b.nodeId),
    );
  }

  function requireInstance(id: string): Instance {
    const instance = instances.get(id);
    if (!instance) {
      throw new ApiError(`Instance "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return instance;
  }

  // ---- Hosts: the unified fleet object (host/deploy UX review, #526) ----
  // Computed on read from the SAME `nodes`/`instances`/`placements` working
  // copies every other method above mutates — never a fourth, independently
  // maintained map (charter A-5: `Host` is a derived view, not a new source
  // of truth). Mirrors `server/internal/hostsvc` (PR #526, merged) field for
  // field and precedence for precedence — `deriveState`/`hostFromNode`/
  // `hostFromInstanceOnly` there are this function's source of truth.

  /** Mirrors `hostsvc.deriveState` exactly (precedence matters — see
   * `HostState`'s doc comment in types.ts for what each value means):
   *   1. A destruction/terminal INSTANCE state wins even over a live node —
   *      once decommissioning is requested/done, that's the truth to show.
   *   2. Otherwise a NODE that exists wins: online only if truly online,
   *      offline for anything else (including "unknown" — a node the agent
   *      has never confirmed live contact from is not "in progress," it's
   *      not answering). This is also the whole BYO/local case.
   *   3. Otherwise (no node yet) the INSTANCE lifecycle describes a host
   *      that hasn't enrolled: error -> provision_failed, requested/
   *      provisioning -> provisioning, running -> enrolling ("running ≠
   *      enrolled" — the provider says it's up, no agent has checked in).
   * `enrolling` therefore only ever occurs with `liveAgent: null` — there is
   * no node to report a live-agent facet for yet (see HostDetailPage, which
   * renders the "enrolling" progress framing inside the PROVISIONING
   * section, not a live-agent one, for exactly this reason). */
  function computeHostState(node: Node | undefined, instance: Instance | undefined): HostState {
    if (instance) {
      if (instance.status === "deleted") return "deleted";
      if (instance.status === "deleting" || instance.status === "deprovision_pending") return "decommissioning";
    }
    if (node) {
      return node.status === "online" ? "online" : "offline";
    }
    if (instance) {
      if (instance.status === "error") return "provision_failed";
      if (instance.status === "requested" || instance.status === "provisioning") return "provisioning";
      if (instance.status === "running") return "enrolling";
    }
    // Defensive only (charter A-3-style honesty): the join below never
    // produces a host with neither facet, so this never fires in practice —
    // never a fabricated "online" if it somehow did.
    return "offline";
  }

  function buildLiveAgentFacet(node: Node): HostLiveAgentFacet {
    return {
      status: node.status,
      agentVersion: node.agentVersion,
      containerCount: node.containerCount,
      lastSeenAt: node.lastSeenAt,
      connectAddress: node.connectAddress,
    };
  }

  function countPlacementsOnNode(nodeId: string): number {
    const distinctServiceIds = new Set<string>();
    for (const placement of placements.values()) {
      if (placement.nodeId === nodeId) distinctServiceIds.add(placement.serviceId);
    }
    return distinctServiceIds.size;
  }

  /** Mirrors `hostsvc.hostFromNode` — a node (managed or BYO/local) fused
   * with its instance (`undefined` for BYO/local). The node supplies
   * identity/name/environment/tags/live-agent facet; the instance (when
   * present) supplies the provisioning facet. */
  function buildHostFromNode(node: Node, instance: Instance | undefined): Host {
    return {
      id: node.id,
      name: node.name,
      hostname: node.hostname,
      environmentId: node.environmentId,
      environmentName: node.environmentName,
      nodeType: node.nodeType,
      tags: node.tags,
      state: computeHostState(node, instance),
      provisioning: instance
        ? {
            provider: instance.providerKind,
            region: instance.region,
            serverType: instance.serverType,
            publicIpv4: instance.publicIpv4,
            lifecycleStatus: instance.status,
          }
        : null,
      liveAgent: buildLiveAgentFacet(node),
      servicesPlacedCount: countPlacementsOnNode(node.id),
    };
  }

  /** Mirrors `hostsvc.hostFromInstanceOnly` — an instance with no enrolled
   * node yet (still provisioning, enrolling, or failed before it came up).
   * No live-agent facet; no environment/node-type (unknown until
   * enrollment); `name`/`id` are the same stable id (instances carry no
   * operator-set name of their own) so identity never changes from the
   * pre-enrollment id to the agent id once enrollment completes. */
  function buildHostFromInstanceOnly(instance: Instance): Host {
    const id = instance.agentId ?? instance.id;
    return {
      id,
      name: id,
      hostname: "",
      environmentId: "",
      environmentName: "",
      nodeType: "",
      tags: [],
      state: computeHostState(undefined, instance),
      provisioning: {
        provider: instance.providerKind,
        region: instance.region,
        serverType: instance.serverType,
        publicIpv4: instance.publicIpv4,
        lifecycleStatus: instance.status,
      },
      liveAgent: null,
      servicesPlacedCount: 0,
    };
  }

  /** Mirrors `hostsvc.joinHosts` — keys instances by their reserved agent id,
   * matches each node to its instance (a managed host), and emits the
   * leftover instances as instance-only hosts (still provisioning/enrolling/
   * failed). Sorted by id (not name) to match the real server's
   * deterministic-pagination order (`hostsvc.sortHosts`). */
  function computeHosts(): Host[] {
    const scopedInstances = Array.from(instances.values()).filter((i) => i.tenant === MOCK_TENANT);
    const scopedNodes = Array.from(nodes.values()).filter((n) => n.tenant === MOCK_TENANT);

    const instanceByAgentId = new Map<string, Instance>();
    const instanceOnly: Instance[] = [];
    for (const instance of scopedInstances) {
      if (instance.agentId) {
        instanceByAgentId.set(instance.agentId, instance);
      } else {
        instanceOnly.push(instance);
      }
    }

    const rows: Host[] = [];
    for (const node of scopedNodes) {
      const linkedInstance = instanceByAgentId.get(node.id);
      instanceByAgentId.delete(node.id); // consumed: this instance has a node
      rows.push(buildHostFromNode(node, linkedInstance));
    }
    for (const instance of instanceByAgentId.values()) {
      rows.push(buildHostFromInstanceOnly(instance));
    }
    for (const instance of instanceOnly) {
      rows.push(buildHostFromInstanceOnly(instance));
    }

    return rows.sort((a, b) => a.id.localeCompare(b.id));
  }

  function requireHost(id: string): Host {
    const host = computeHosts().find((h) => h.id === id);
    if (!host) {
      throw new ApiError(`Host "${id}" was not found.`, 404, "not_found");
    }
    return host;
  }

  // 1..63 chars, alphanumeric/'.'/'_'/'-' after an alphanumeric first
  // character — used for project/service/instance names, matching the
  // server's resource-name allow-list.
  function validResourceName(name: string): boolean {
    if (name.length === 0 || name.length > 63) return false;
    return /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(name);
  }

  // ---- First-class environments (ADR-0035 §1) lookup guard ----
  // Tenant-implicit (this mock is single-tenant): an unknown id is a 404,
  // indistinguishable from a cross-tenant one on the real server (S-17).
  function requireEnvironment(envId: string): Environment {
    const env = environments.get(envId);
    if (!env) {
      throw new ApiError(`Environment "${envId}" was not found.`, 404, "not_found");
    }
    return env;
  }

  /** Resolves an operator-supplied environment SELECTOR (an id or a display
   * name) the same way `Service.resolveTargetEnvironment` does server-side
   * (server/internal/nodesvc/nodesvc.go) — id first, then name — so the mock
   * accepts exactly the same "Change environment" input the real server
   * does (A-5: one resolution rule, not a client-only id-only shortcut). */
  function resolveEnvironmentSelector(selector: string): Environment {
    const byId = environments.get(selector);
    if (byId) return byId;
    for (const env of environments.values()) {
      if (env.name === selector) return env;
    }
    throw new ApiError(`Environment "${selector}" was not found.`, 404, "not_found");
  }

  /** Mirrors `store.EnvironmentClass.IsProduction` (server/internal/store/
   * model.go): today "production" is the only production-tier class, kept as
   * its own predicate (not an inlined `=== "production"` at every call site)
   * so a second production tier only needs to change here. */
  function isProductionClass(envClass: EnvironmentClass): boolean {
    return envClass === "production";
  }

  // ---- The Placement Graph (ADR-0025) lookup guards ----

  function requireNode(nodeId: string): Node {
    const node = nodes.get(nodeId);
    // Single-tenant mock (ADR-0035 M1 PR-D): a node whose seed tenant is not the
    // implicit tenant is invisible, exactly as a cross-tenant node 404s server-side (S-17).
    if (!node || node.tenant !== MOCK_TENANT) {
      throw new ApiError(`Node "${nodeId}" was not found.`, 404, "not_found");
    }
    return node;
  }

  // Mirrors `validateConnectAddress` (server/internal/nodesvc/nodesvc.go)
  // closely enough to give an operator the same "this needs to look like
  // host or host:port" signal before the round trip — the server remains
  // the actual gate (S-8).
  function validateConnectAddress(raw: string): string {
    const addr = raw.trim();
    if (addr === "") return "";
    if (addr.length > 253) {
      throw new ApiError("connectAddress exceeds 253 bytes", 400, "invalid_connect_address");
    }
    if (/[\x00-\x1f\x7f ]/.test(addr)) {
      throw new ApiError("connectAddress must not contain whitespace or control characters", 400, "invalid_connect_address");
    }
    const lastColon = addr.lastIndexOf(":");
    if (lastColon !== -1 && addr.indexOf(":") === lastColon) {
      // Exactly one colon — treat it as host:port (mirrors net.SplitHostPort
      // for the common IPv4/DNS-name case; a bracketed IPv6 literal with a
      // port has more than one colon after the bracket and is left as a bare
      // host below, same as the server's `net.SplitHostPort` would only
      // split it with an explicit `[::1]:port` form).
      const host = addr.slice(0, lastColon);
      const portStr = addr.slice(lastColon + 1);
      const port = Number(portStr);
      if (host === "") {
        throw new ApiError("connectAddress has an empty host", 400, "invalid_connect_address");
      }
      if (!Number.isInteger(port) || port < 1 || port > 65535) {
        throw new ApiError("connectAddress port is out of range", 400, "invalid_connect_address");
      }
    }
    return addr;
  }

  function requirePlacement(projectId: string, serviceId: string, placementId: string): Placement {
    // Confirms the service actually belongs to this fleet/project first — a
    // placement id that's real but under the wrong service/project/fleet
    // 404s the same as one that doesn't exist at all, matching
    // requireService's own posture.
    requireService(projectId, serviceId);
    const placement = placements.get(placementId);
    if (!placement || placement.serviceId !== serviceId) {
      throw new ApiError(`Placement "${placementId}" was not found for that service.`, 404, "not_found");
    }
    return placement;
  }

  // A connection doesn't carry its own project/fleet id (see Connection in
  // types.ts) — its scope is derived from its `fromService`, which does.
  function connectionInScope(projectId: string, connection: Connection): boolean {
    const from = services.get(connection.fromService);
    return Boolean(from && from.projectId === projectId);
  }

  function requireConnection(projectId: string, connectionId: string): Connection {
    const connection = connections.get(connectionId);
    if (!connection || !connectionInScope(projectId, connection)) {
      throw new ApiError(`Connection "${connectionId}" was not found in that project.`, 404, "not_found");
    }
    return connection;
  }

  // ---- Service domain bindings (ADR-0026 §3/§5) lookup guard ----

  function requireDomain(projectId: string, serviceId: string, domainId: string): Domain {
    requireService(projectId, serviceId);
    const domain = domains.get(domainId);
    if (!domain || domain.serviceId !== serviceId) {
      throw new ApiError(`Domain "${domainId}" was not found for that service.`, 404, "not_found");
    }
    return domain;
  }

  // Same RFC-1123 shape the server enforces (projectsvc.validateHostname):
  // no scheme/path/port/whitespace, at least two dot-separated labels, each
  // 1..63 chars of [A-Za-z0-9-] with no leading/trailing hyphen. Kept as a
  // second, independent implementation (not shared code with the server —
  // there's no such shared module across the Go/TS boundary) so the mock
  // exercises the same refusal shape a real 400 would.
  function validHostnameShape(hostname: string): boolean {
    if (hostname.length === 0 || hostname.length > 253) return false;
    if (/[/:@\s]/.test(hostname)) return false;
    const labels = hostname.split(".");
    if (labels.length < 2) return false;
    return labels.every((label) => /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(label));
  }

  return {

    async login({ username, password }: LoginInput): Promise<Session> {
      if (!username.trim() || !password) {
        throw new ApiError("Enter a username and password to sign in.", 401, "invalid_credentials");
      }
      // Stub only — real auth (C-3 local bootstrap, C-5 OIDC) issues a
      // server-set session cookie; the browser never handles a token here.
      // Keeps `account` (used by getAccount/changePassword and the "is this
      // me" guards on the users list) in sync with whoever "signed in."
      account = { ...account, username: username.trim(), displayName: username.trim() };
      return delay({ userId: account.id, displayName: account.displayName, role: account.role });
    },

    async register({ username, password, displayName }: RegisterInput): Promise<Session> {
      if (!username.trim() || !password || !displayName.trim()) {
        throw new ApiError("All fields are required.", 400, "validation_error");
      }
      account = { ...account, username: username.trim(), displayName: displayName.trim() };
      return delay({ userId: account.id, displayName: account.displayName, role: account.role });
    },

    async logout(): Promise<void> {
      await delay(undefined, 80);
    },

    async getSession(): Promise<Session | null> {
      // The mock has no server-set cookie to recover a session from — its
      // whole "who's logged in" state is the in-memory `account` object
      // above, which only exists after a `login()` in THIS page load. So
      // this always resolves null, same as the real client falls back to
      // while its assumed `/api/auth/session` endpoint doesn't exist yet
      // (see ApiClient.getSession's doc comment) — exercising exactly the
      // "gracefully degrades" path both today.
      return delay(null, 60);
    },

    async listContainers(params: ListContainersParams): Promise<Page<Container>> {
      const scoped = Array.from(containers.values()).filter(
        (c) =>
          (!params.nodeId || c.nodeId === params.nodeId) &&
          // Mirrors the server's `?unmanaged=true` filter (ADR-0038 P4a): omitted/false
          // returns every container, unchanged from before the filter existed.
          (!params.unmanaged || !c.managed) &&
          // Mirrors the server's `?serviceId=` filter (#408): omitted returns every
          // container regardless of owning service.
          (!params.serviceId || c.service?.serviceId === params.serviceId),
      );
      scoped.sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getContainer(nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(nodeId, containerId);
      return delay(clone(container));
    },

    async startContainer(nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(nodeId, containerId);
      const updated = await delay(
        { ...container, state: "running" as const, status: "Up less than a minute", startedAt: new Date().toISOString() },
        350,
      );
      containers.set(containerId, updated);
      emit({
        type: "state-changed",
        containerId,
        nodeId,
        tenant: MOCK_TENANT,
        state: updated.state,
        status: updated.status,
      });
      return clone(updated);
    },

    async stopContainer(nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(nodeId, containerId);
      const updated = await delay(
        { ...container, state: "exited" as const, status: "Exited (0) less than a minute ago" },
        350,
      );
      containers.set(containerId, updated);
      emit({
        type: "state-changed",
        containerId,
        nodeId,
        tenant: MOCK_TENANT,
        state: updated.state,
        status: updated.status,
      });
      return clone(updated);
    },

    async restartContainer(nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(nodeId, containerId);
      // Two-phase, like a real restart: caller gets "restarting" back almost
      // immediately, then a `state-changed` event lands ~2.5s later once the
      // container is back up — this is the seam event-driven updates (P-1)
      // will attach to once a real tunnel/event stream exists.
      const transitional: Container = { ...container, state: "restarting", status: "Restarting (0) less than a second ago" };
      containers.set(containerId, transitional);
      const result = await delay(transitional, 200);

      setTimeout(() => {
        const current = containers.get(containerId);
        if (!current || current.state !== "restarting") return; // stopped/removed meanwhile
        const restarted: Container = {
          ...current,
          state: "running",
          status: "Up less than a minute",
          startedAt: new Date().toISOString(),
        };
        containers.set(containerId, restarted);
        emit({
          type: "state-changed",
          containerId,
          nodeId,
          tenant: MOCK_TENANT,
          state: restarted.state,
          status: restarted.status,
        });
      }, 2500);

      return clone(result);
    },

    async listNodes(params: ListNodesParams): Promise<Page<Node>> {
      const scoped = Array.from(nodes.values())
        .filter((n) => n.tenant === MOCK_TENANT)
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    // ---- Hosts: the unified fleet object (host/deploy UX review, #526) ----
    async listHosts(params: ListHostsParams): Promise<Page<Host>> {
      return delay(paginate(clone(computeHosts()), params));
    },

    async getHost(id: string): Promise<Host> {
      return delay(clone(requireHost(id)));
    },

    async updateNode(nodeId: string, input: UpdateNodeInput): Promise<Node> {
      const existing = requireNode(nodeId);
      if (input.connectAddress === undefined) {
        throw new ApiError("no editable field supplied", 400, "invalid_request");
      }
      const addr = validateConnectAddress(input.connectAddress);
      const updated: Node = { ...existing, connectAddress: addr };
      nodes.set(nodeId, updated);
      return delay(clone(updated), 200);
    },

    async setNodeEnvironment(nodeId: string, input: SetNodeEnvironmentInput): Promise<Node> {
      const existing = requireNode(nodeId);
      const selector = input.environmentId.trim();
      if (!selector) {
        throw new ApiError("environmentId is required", 400, "invalid_request");
      }
      const target = resolveEnvironmentSelector(selector);
      // ADR-0034 SAFETY INVARIANT, mirrored from the server's
      // SetNodeEnvironmentHandler: a `local` node can never be attached to a
      // production-class environment.
      if (existing.nodeType === "local" && isProductionClass(target.class)) {
        throw new ApiError(
          "a local node cannot be moved into a production-class environment (ADR-0034)",
          409,
          "conflict",
        );
      }
      const updated: Node = { ...existing, environmentId: target.id, environmentName: target.name };
      nodes.set(nodeId, updated);
      return delay(clone(updated), 200);
    },

    async listSecrets(params: ListSecretsParams): Promise<Page<SecretSummary>> {
      const scoped = Array.from(secrets.values())
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async createSecret(input: CreateSecretInput): Promise<SecretSummary> {
      if (!input.name.trim()) {
        throw new ApiError("Name must be 1..253 characters.", 400, "invalid_secret");
      }
      if (!input.value) {
        throw new ApiError("Value must not be empty.", 400, "invalid_secret");
      }
      secretSeq += 1;
      const now = new Date().toISOString();
      // Note: `input.value` is intentionally discarded after this point —
      // the mock doesn't even hold onto it, matching the write-only
      // contract (S-13) the real server must implement (encrypt & store,
      // never echo back).
      const summary: SecretSummary = {
        id: `secret-mock-${secretSeq}`,
        name: input.name,
        version: 1,
        tenant: MOCK_TENANT,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      secrets.set(summary.id, summary);
      return delay(clone(summary));
    },

    async rotateSecret(secretId: string, value: string): Promise<SecretSummary> {
      const existing = secrets.get(secretId);
      if (!existing) {
        throw new ApiError(`Secret "${secretId}" was not found in this fleet.`, 404, "not_found");
      }
      if (!value) {
        throw new ApiError("Value must not be empty.", 400, "invalid_secret");
      }
      // `value` is discarded here too — the mock never holds a secret value,
      // matching the real service's write-only contract (S-13/S-14).
      const rotated: SecretSummary = {
        ...existing,
        version: existing.version + 1,
        updatedAt: new Date().toISOString(),
      };
      secrets.set(secretId, rotated);
      return delay(clone(rotated), 300);
    },

    async deleteSecret(secretId: string): Promise<void> {
      secrets.delete(secretId);
      await delay(undefined, 150);
    },

    async createEnrollToken(input: CreateEnrollTokenInput): Promise<EnrollToken> {
      // Mirrors the server's `resolveEnrollEnvironment` (server/internal/enroll/
      // enroll.go): the environment is a REQUIRED NAME (never an id), resolved
      // tenant-scoped — an empty or unresolvable name is refused with a 400,
      // exactly like the server (enrollment no longer defaults to production,
      // ADR-0035 §1 / #605 — see CreateEnrollTokenInput's doc comment).
      const name = input.environment.trim();
      if (!name) {
        throw new ApiError(
          "an environment is required (name it explicitly); enrollment no longer defaults to production",
          400,
          "invalid_request",
        );
      }
      const env = Array.from(environments.values()).find((e) => e.name === name);
      if (!env) {
        throw new ApiError(`No environment "${name}" in this tenant.`, 400, "invalid_request");
      }
      const nodeType = input.nodeType ?? "";
      if (nodeType === "local" && isProductionClass(env.class)) {
        // Mirrors `refuseLocalInProduction` server-side (ADR-0034).
        throw new ApiError("a local node can never join a production-class environment", 409, "conflict");
      }

      // A believable-looking opaque token — never a real credential, and never
      // logged or persisted anywhere by this mock.
      const token = `jtok_mock_${MOCK_TENANT}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString(); // 15 min, mirrors a short-lived join token

      // Echoes back whatever agent id the caller pre-assigned (PR #634
      // security audit condition 5) — mirrors the server's `createTokenResponse.
      // AgentID`, which is exactly the id the caller sent, never server-minted.
      // Falls back to a mock-generated one when the caller omitted it, so the
      // laptop-connects simulation below always has an id to use.
      let agentId = input.agentId?.trim();
      if (nodeType === "local") {
        // Simulates the operator actually running the printed `kelpie -local`
        // command on their own machine (deploy wizard's "use this laptop" step,
        // journey.md rung 3/G13) — without this, the wizard's poll-for-a-new-
        // local-node loop would spin forever in demo/mock mode.
        if (!agentId) {
          localNodeSeq += 1;
          agentId = `node-mock-local-${localNodeSeq}`;
        }
        const resolvedAgentId = agentId;
        const label = input.label?.trim() || "local";
        setTimeout(() => {
          if (nodes.has(resolvedAgentId)) return;
          nodes.set(resolvedAgentId, {
            id: resolvedAgentId,
            name: label,
            hostname: label,
            status: "online",
            agentVersion: "0.4.2",
            tenant: MOCK_TENANT,
            containerCount: 0,
            lastSeenAt: new Date().toISOString(),
            tags: [],
            connectAddress: "",
            environmentId: env.id,
            environmentName: env.name,
            nodeType: "local",
          });
        }, localEnrollDelayMs);
      }

      return delay({ token, expiresAt, agentId }, 300);
    },

    // ---- Live log tail (C-8a) ----
    // Simulates the SSE contract with a timer: emits a synthetic stdout/stderr
    // line every ~450-850ms and, after a bounded run, a terminal "stopped"
    // end event — so the end-of-stream and bounded-buffer/auto-scroll paths
    // are all reachable in demo/mock mode without a real backend.

    openLogStream({ containerId }: OpenLogStreamParams): LogStreamHandle {
      let seq = 0;
      // 50-89 lines — long enough to exercise scrollback/auto-scroll, short
      // enough that a developer testing this by hand sees the `end` event
      // land within roughly a minute.
      const endAfterLines = 50 + Math.floor(Math.random() * 40);

      const emitter = createLogStreamEmitter(() => clearInterval(timer));

      const timer = setInterval(() => {
        const base = MOCK_LOG_LINES[seq % MOCK_LOG_LINES.length];
        seq += 1;
        emitter.emitLine({ stream: base.stream, message: `${base.message} (container ${containerId}, #${seq})` });
        if (seq >= endAfterLines) {
          emitter.emitEnd("stopped");
        }
      }, 450 + Math.round(Math.random() * 400));

      // Deferred to the next tick so a caller that subscribes via
      // onStatus/onLine/onEnd right after receiving this handle (the normal
      // call pattern) doesn't miss it — emitting synchronously here would
      // race ahead of that subscription.
      setTimeout(() => emitter.emitStatus("open"), 120);

      return emitter;
    },

    // ---- Interactive exec (SPIKE-3 / C-8) ----
    // Simulates the browser-WS-to-Session bridge with a tiny in-memory shell:
    // enough of a REPL (prompt, backspace editing, a handful of recognized
    // commands, `exit`) to prototype the terminal component's xterm.js wiring
    // against a real ExecStreamHandle before the server bridge exists.

    openExecStream({ containerId, cols }: OpenExecStreamParams): ExecStreamHandle {
      const textEncoder = new TextEncoder();
      const textDecoder = new TextDecoder();
      const prompt = `root@${containerId.slice(0, 12)}:~# `;
      let lineBuffer = "";
      let terminalCols = cols;

      const emitter = createExecStreamEmitter(
        () => {
          // Nothing to tear down beyond the emitter's own bookkeeping — no
          // timer runs between keystrokes, unlike openLogStream's interval.
        },
        (data) => {
          const text = textDecoder.decode(data);
          for (const char of text) {
            if (char === "\r" || char === "\n") {
              emitter.emitOutput(textEncoder.encode("\r\n"));
              runCommand(lineBuffer.trim());
              lineBuffer = "";
              continue;
            }
            if (char === "\x7f") {
              // Backspace: only erase if there's something to erase — a
              // real terminal driver refuses to back up past the prompt.
              if (lineBuffer.length > 0) {
                lineBuffer = lineBuffer.slice(0, -1);
                emitter.emitOutput(textEncoder.encode("\b \b"));
              }
              continue;
            }
            if (char === "\x03") {
              // Ctrl-C: abandon the current line, fresh prompt.
              emitter.emitOutput(textEncoder.encode("^C\r\n" + prompt));
              lineBuffer = "";
              continue;
            }
            lineBuffer += char;
            emitter.emitOutput(textEncoder.encode(char));
          }
        },
        (nextCols) => {
          terminalCols = nextCols;
        },
      );

      function runCommand(cmd: string) {
        if (cmd === "exit") {
          emitter.emitEnd("exited");
          return;
        }
        const output = MOCK_SHELL_RESPONSES[cmd];
        if (cmd.length > 0) {
          const line =
            output !== undefined ? `${output.replace(/\n/g, "\r\n")}\r\n` : `bash: ${cmd}: command not found\r\n`;
          emitter.emitOutput(textEncoder.encode(line));
        }
        emitter.emitOutput(textEncoder.encode(prompt));
      }

      // Deferred to the next tick, same reasoning as openLogStream's status
      // emit — so a caller subscribing right after receiving the handle
      // (the normal call pattern) doesn't miss the banner/prompt.
      setTimeout(() => {
        emitter.emitStatus("open");
        emitter.emitOutput(
          textEncoder.encode(
            `Connected to ${containerId} (${terminalCols} cols) — mock shell, type 'exit' to close.\r\n${prompt}`,
          ),
        );
      }, 120);

      return emitter;
    },

    subscribeToContainerEvents(onEvent: EventHandler): () => void {
      const set = subscribers.get(MOCK_TENANT) ?? new Set<EventHandler>();
      set.add(onEvent);
      subscribers.set(MOCK_TENANT, set);
      return () => {
        set.delete(onEvent);
      };
    },

    // ---- Admin: users (admin/user portal) ----

    async listUsers(params: PageParams): Promise<Page<User>> {
      const list = Array.from(users.values()).sort((a, b) => a.username.localeCompare(b.username));
      return delay(paginate(clone(list), params));
    },

    async createUser(input: CreateUserInput): Promise<User> {
      const username = input.username.trim();
      const displayName = input.displayName.trim();
      if (!validUsername(username)) {
        throw new ApiError(
          "Username must be 1..64 characters: letters, digits, '.', '_', '-' only.",
          400,
          "invalid_user",
        );
      }
      if (!displayName) {
        throw new ApiError("Display name is required.", 400, "invalid_user");
      }
      if (!input.password || input.password.length < 8) {
        throw new ApiError("Password must be at least 8 characters.", 400, "invalid_user");
      }
      if (Array.from(users.values()).some((u) => u.username.toLowerCase() === username.toLowerCase())) {
        throw new ApiError(`A user named "${username}" already exists.`, 409, "conflict");
      }
      userSeq += 1;
      const now = new Date().toISOString();
      // `input.password` is intentionally discarded after validation — the
      // mock never holds it, matching the real service's write-only
      // contract for credentials (S-13's spirit).
      const created: User = {
        id: `usr-mock-${userSeq}`,
        username,
        displayName,
        role: input.role,
        active: true,
        createdAt: now,
      };
      users.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateUser(id: string, input: UpdateUserInput): Promise<User> {
      const existing = users.get(id);
      if (!existing) {
        throw new ApiError(`User "${id}" was not found.`, 404, "not_found");
      }
      const deactivatingSelf = id === account.id && input.active === false;
      if (deactivatingSelf) {
        throw new ApiError("You can't deactivate your own account.", 409, "self_action");
      }
      const losingLastAdmin =
        existing.role === "admin" &&
        existing.active &&
        activeAdminCount() <= 1 &&
        ((input.active === false) || (input.role !== undefined && input.role !== "admin"));
      if (losingLastAdmin) {
        throw new ApiError("Can't remove the last admin. Promote another user to admin first.", 409, "last_admin");
      }
      const updated: User = {
        ...existing,
        displayName: input.displayName?.trim() || existing.displayName,
        role: input.role ?? existing.role,
        active: input.active ?? existing.active,
      };
      users.set(id, updated);
      return delay(clone(updated), 250);
    },

    async resetUserPassword(id: string, password: string): Promise<void> {
      if (!users.has(id)) {
        throw new ApiError(`User "${id}" was not found.`, 404, "not_found");
      }
      if (!password || password.length < 8) {
        throw new ApiError("Password must be at least 8 characters.", 400, "invalid_user");
      }
      // Discarded, not stored — same write-only contract as createUser above.
      await delay(undefined, 250);
    },

    async deleteUser(id: string): Promise<void> {
      const existing = users.get(id);
      if (!existing) {
        throw new ApiError(`User "${id}" was not found.`, 404, "not_found");
      }
      if (id === account.id) {
        throw new ApiError("You can't remove your own account.", 409, "self_action");
      }
      if (existing.role === "admin" && existing.active && activeAdminCount() <= 1) {
        throw new ApiError("Can't remove the last admin. Promote another user to admin first.", 409, "last_admin");
      }
      users.delete(id);
      await delay(undefined, 250);
    },

    // ---- OIDC / SSO admin (ADR-0037 PR-2b) ----

    async getSSOConfig(): Promise<SSOConfig> {
      return delay(clone(ssoConfig));
    },

    async updateSSOConfig(input: SSOConfigInput): Promise<SSOConfig> {
      const issuer = input.issuer.trim();
      const clientId = input.clientId.trim();
      if (!issuer || !clientId) {
        throw new ApiError("Issuer and client ID are required.", 400, "invalid_sso_config");
      }
      if (!/^https:\/\//.test(issuer)) {
        throw new ApiError("Issuer must be an https URL.", 400, "invalid_sso_config");
      }
      // Mirrors the real write-only contract exactly: an empty clientSecret
      // leaves whatever was already sealed untouched; any non-empty value
      // "reseals" it. The value itself is discarded either way (S-13) — this
      // mock never holds a client secret in memory a moment longer than this
      // call.
      const secretSet = input.clientSecret ? true : ssoConfig.secretSet;
      ssoConfig = { configured: true, issuer, clientId, enabled: input.enabled, secretSet, callbackUrl: ssoConfig.callbackUrl };
      return delay(clone(ssoConfig), 250);
    },

    async testSSOConnection(input: SSOTestConnectionInput): Promise<SSOTestConnectionResult> {
      const issuer = input.issuer.trim();
      const clientId = input.clientId.trim();
      if (!issuer || !clientId) {
        return delay({ ok: false, message: "Enter an issuer and client ID before testing." }, 200);
      }
      if (!/^https:\/\//.test(issuer)) {
        return delay({ ok: false, message: "Issuer must be an https URL." }, 200);
      }
      // No real IdP to reach in the mock — any well-formed https issuer +
      // non-empty client id "discovers" successfully, so the demo build can
      // exercise the test-before-enable gate without a live identity provider.
      return delay({ ok: true, message: `Discovery and JWKS reachable at ${issuer}.` }, 500);
    },

    async createSSOInvite(input: SSOInviteInput): Promise<SSOInvite> {
      const username = input.username.trim();
      const displayName = input.displayName?.trim() || username;
      const email = input.email.trim().toLowerCase();
      if (!validUsername(username)) {
        throw new ApiError(
          "Username must be 1..64 characters: letters, digits, '.', '_', '-' only.",
          400,
          "invalid_invite",
        );
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new ApiError("A valid email is required.", 400, "invalid_invite");
      }
      const usernameTaken = Array.from(users.values()).some((u) => u.username.toLowerCase() === username.toLowerCase());
      const emailTaken = Array.from(ssoInviteEmailByUserId.values()).some((e) => e === email);
      if (usernameTaken || emailTaken) {
        throw new ApiError("A user with that username or email already exists.", 409, "conflict");
      }
      ssoInviteSeq += 1;
      const now = new Date().toISOString();
      const created: User = {
        id: `usr-sso-invite-${ssoInviteSeq}`,
        username,
        displayName,
        role: "viewer", // ADR-0037 §6: every invite is viewer-default, raised manually afterward
        active: false,
        createdAt: now,
        authMethod: "sso",
        status: "invited",
      };
      users.set(created.id, created);
      ssoInviteEmailByUserId.set(created.id, email);
      return delay(
        {
          id: created.id,
          username: created.username,
          displayName: created.displayName,
          email,
          role: "viewer",
          status: "invited",
          authMethod: "sso",
        },
        300,
      );
    },

    // ---- Account (the caller's own profile/credential) ----

    async getAccount(): Promise<Account> {
      return delay(clone(account));
    },

    async changePassword(input: ChangePasswordInput): Promise<void> {
      if (!input.currentPassword) {
        throw new ApiError("Enter your current password.", 400, "invalid_password");
      }
      if (!input.newPassword || input.newPassword.length < 8) {
        throw new ApiError("New password must be at least 8 characters.", 400, "invalid_password");
      }
      if (input.newPassword === input.currentPassword) {
        throw new ApiError("New password must be different from your current password.", 400, "invalid_password");
      }
      // Neither password is stored anywhere — discarded once "validated"
      // here, matching the real endpoint's write-only contract.
      await delay(undefined, 300);
    },

    // ---- Audit log (C-13) ----

    async listAuditEvents(params: ListAuditParams): Promise<Page<AuditEvent>> {
      const events = auditEventsFor();
      // Mirrors the server's `?action=` filter (auditsvc/http.go): applied here,
      // in the mock, before pagination — same shape as listContainers' nodeId
      // filter — so a filtered page is genuinely complete, not fetched-then-cut.
      const scoped = params.action ? events.filter((e) => e.action === params.action) : events;
      return delay(paginate(clone(scoped), params));
    },

    async verifyAuditChain(): Promise<AuditVerification> {
      const events = auditEventsFor();
      return delay({ ok: true, status: "ok", count: events.length, tenant: MOCK_TENANT }, 500);
    },

    // ---- Hero flow: projects (#63 projectsvc — real, merged contract) ----

    async listProjects(params: ListProjectsParams): Promise<Page<Project>> {
      const scoped = Array.from(projects.values())
        .filter((p) => p.tenant === MOCK_TENANT)
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getProject(id: string): Promise<Project> {
      return delay(clone(requireProject(id)));
    },

    async createProject(input: CreateProjectInput): Promise<Project> {
      const name = input.name.trim();
      if (!validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_project",
        );
      }
      if (Array.from(projects.values()).some((p) => p.name === name)) {
        throw new ApiError(`A project named "${name}" already exists in this fleet.`, 409, "conflict");
      }
      projectSeq += 1;
      const now = new Date().toISOString();
      const created: Project = {
        id: `proj-mock-${projectSeq}`,
        name,
        tenant: MOCK_TENANT,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      projects.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
      const existing = requireProject(id);
      const name = input.name !== undefined ? input.name.trim() : existing.name;
      if (input.name !== undefined && !validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_project",
        );
      }
      const updated: Project = {
        ...existing,
        name,
        updatedAt: new Date().toISOString(),
      };
      projects.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteProject(id: string): Promise<void> {
      requireProject(id);
      // Cascades to the project's services, their deployment history, and
      // (ADR-0025) every placement/connection built on top of them —
      // mirroring DeleteProjectHandler.
      for (const svc of Array.from(services.values())) {
        if (svc.projectId === id) {
          services.delete(svc.id);
          for (const dep of Array.from(deployments.values())) {
            if (dep.serviceId === svc.id) deployments.delete(dep.id);
          }
          for (const plc of Array.from(placements.values())) {
            if (plc.serviceId === svc.id) placements.delete(plc.id);
          }
          for (const conn of Array.from(connections.values())) {
            if (conn.fromService === svc.id || conn.toService === svc.id) connections.delete(conn.id);
          }
        }
      }
      // A deleted project's corpus binding/projection go with it — there is nothing left
      // to sync into.
      corpusBindings.delete(id);
      corpusArtifacts.delete(id);
      projects.delete(id);
      await delay(undefined, 250);
    },

    // ---- Corpus (ADR-0052 read / ADR-0053 §3 binding) ----

    async getCorpus(projectId: string, params: PageParams = {}): Promise<CorpusList> {
      // Mirrors corpussvc.ListAppliedArtifacts: an unknown/cross-tenant project has no
      // applied snapshot, so it reads as an empty page — never a 404 — same as a known
      // project that simply hasn't synced yet (S-16/S-17/A-10).
      const artifacts = corpusArtifacts.get(projectId) ?? [];
      const page = paginate(clone(artifacts), params);
      const sourceCommit = artifacts[0]?.sourceCommit ?? "";
      // A never-synced project OMITS syncedAt entirely, exactly as corpussvc does — never a
      // fabricated/zero time (A-10).
      const syncedAt = corpusSyncedAt.get(projectId);
      return delay({ sourceCommit, ...(syncedAt ? { syncedAt } : {}), ...page });
    },

    async getCorpusBinding(projectId: string): Promise<CorpusBinding> {
      const binding = corpusBindings.get(projectId);
      if (!binding) {
        throw new ApiError(`Project "${projectId}" has no corpus binding yet.`, 404, "not_found");
      }
      return delay(clone(binding));
    },

    async createCorpusBinding(projectId: string, input: CorpusBindingInput): Promise<CorpusBinding> {
      requireProject(projectId);
      const policy = validateCorpusBindingInput(input);
      if (corpusBindings.has(projectId)) {
        throw new ApiError(`Project "${projectId}" already has a corpus binding.`, 409, "conflict");
      }
      for (const existing of corpusBindings.values()) {
        if (existing.repoRef === policy.repoRef) {
          throw new ApiError(`Repository "${policy.repoRef}" is already bound to another project.`, 409, "conflict");
        }
      }
      const now = new Date().toISOString();
      const created: CorpusBinding = {
        projectId,
        ...policy,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      corpusBindings.set(projectId, created);
      return delay(clone(created), 300);
    },

    async updateCorpusBinding(projectId: string, input: CorpusBindingInput): Promise<CorpusBinding> {
      const existing = corpusBindings.get(projectId);
      if (!existing) {
        throw new ApiError(`Project "${projectId}" has no corpus binding yet.`, 404, "not_found");
      }
      const policy = validateCorpusBindingInput(input);
      for (const [otherProjectId, other] of corpusBindings) {
        if (otherProjectId !== projectId && other.repoRef === policy.repoRef) {
          throw new ApiError(`Repository "${policy.repoRef}" is already bound to another project.`, 409, "conflict");
        }
      }
      const updated: CorpusBinding = {
        ...existing,
        ...policy,
        updatedAt: new Date().toISOString(),
      };
      corpusBindings.set(projectId, updated);
      return delay(clone(updated), 250);
    },

    async listGovernanceFindings(projectId: string, params: PageParams = {}): Promise<Page<GovernanceFinding>> {
      const scoped = Array.from(governanceFindings.values())
        .filter((f) => f.projectId === projectId)
        // Newest first, mirroring the server's produced_at DESC ordering.
        .sort((a, b) => (a.producedAt < b.producedAt ? 1 : -1));
      return delay(paginate(clone(scoped), params));
    },

    async resolveGovernanceFinding(id: string, note: string): Promise<GovernanceFinding> {
      const existing = governanceFindings.get(id);
      if (!existing) {
        throw new ApiError(`No such governance finding "${id}".`, 404, "not_found");
      }
      if (existing.status === "resolved") {
        // open->resolved is terminal, mirroring the server's ErrConflict.
        throw new ApiError(`Governance finding "${id}" is already resolved.`, 409, "conflict");
      }
      const resolved: GovernanceFinding = {
        ...existing,
        status: "resolved",
        resolvedBy: "you",
        resolvedAt: new Date().toISOString(),
        resolutionNote: note,
      };
      governanceFindings.set(id, resolved);
      return delay(clone(resolved), 250);
    },

    async getGovernancePolicy(projectId: string): Promise<GovernancePolicy> {
      requireProject(projectId);
      // No entry reads as default-off, mirroring the server's zero-value GovernancePolicy for a
      // project that declares no governance rule at all (never fabricate enforcement, A-10).
      const policy = GOVERNANCE_POLICIES[projectId] ?? { governanceConfigured: false, predicates: [] };
      return delay(clone(policy));
    },

    // ---- Hero flow: services (create/update/deploy/deployment history) ----

    async listServices(params: ListServicesParams): Promise<Page<Service>> {
      requireProject(params.projectId);
      const scoped = Array.from(services.values())
        .filter(
          (s) =>
            s.tenant === MOCK_TENANT &&
            s.projectId === params.projectId &&
            // Mirrors the server's `?createdBy=` filter (#408) — additive to the
            // tenant+project scoping above, never a substitute for it (S-8).
            (!params.createdBy || s.createdBy === params.createdBy),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getService(projectId: string, id: string): Promise<Service> {
      return delay(clone(requireService(projectId, id)));
    },

    async createService(projectId: string, input: CreateServiceInput): Promise<Service> {
      requireProject(projectId);
      const name = input.name.trim();
      if (!validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_service",
        );
      }
      if (!Number.isInteger(input.desiredCount) || input.desiredCount < 0) {
        throw new ApiError("Desired count must be a non-negative integer.", 400, "invalid_service");
      }
      if (
        Array.from(services.values()).some(
          (s) => s.projectId === projectId && s.name === name,
        )
      ) {
        throw new ApiError(`A service named "${name}" already exists in this project.`, 409, "conflict");
      }
      serviceSeq += 1;
      const now = new Date().toISOString();
      const created: Service = {
        id: `svc-mock-${serviceSeq}`,
        projectId,
        tenant: MOCK_TENANT,
        name,
        image: input.image?.trim() || undefined,
        env: input.env ?? {},
        secretRefs: input.secretRefs ?? [],
        desiredCount: input.desiredCount,
        // Mirrors `initialServiceStatus` server-side: a freshly created
        // service is only ever "created" until it is actually deployed (see
        // deployService below, which flips it to "running"/left at
        // "created" on a refusal, or would flip it to "error" past that).
        status: "created",
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      services.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateService(
            projectId: string,
      id: string,
      input: UpdateServiceInput,
    ): Promise<Service> {
      const existing = requireService(projectId, id);
      if (input.desiredCount !== undefined && (!Number.isInteger(input.desiredCount) || input.desiredCount < 0)) {
        throw new ApiError("Desired count must be a non-negative integer.", 400, "invalid_service");
      }
      const updated: Service = {
        ...existing,
        image: input.image !== undefined ? input.image.trim() || undefined : existing.image,
        env: input.env ?? existing.env,
        secretRefs: input.secretRefs ?? existing.secretRefs,
        desiredCount: input.desiredCount ?? existing.desiredCount,
        updatedAt: new Date().toISOString(),
      };
      services.set(id, updated);
      return delay(clone(updated), 250);
    },

    // Also the un-adopt path (ADR-0038 §3): deleting a service adopt created never
    // stops/removes its bound container (adoption never relabeled it, and delete only
    // ever touches this mock's own metadata maps below) — the container keeps running,
    // unmanaged again. Frees the adopt bookkeeping (see the block above the `return`)
    // so the same container can be re-adopted afterward, mirroring the real
    // `(tenant, container_id)` constraint being released when its row is deleted.
    async deleteService(projectId: string, id: string): Promise<void> {
      requireService(projectId, id);
      for (const dep of Array.from(deployments.values())) {
        if (dep.serviceId === id) deployments.delete(dep.id);
      }
      // ADR-0025: a deleted service takes its placements and any connection
      // touching it down with it — neither can meaningfully reference a
      // service that no longer exists.
      for (const plc of Array.from(placements.values())) {
        if (plc.serviceId === id) placements.delete(plc.id);
      }
      for (const conn of Array.from(connections.values())) {
        if (conn.fromService === id || conn.toService === id) connections.delete(conn.id);
      }
      const adoptedContainerId = serviceAdoptedContainer.get(id);
      if (adoptedContainerId) {
        adoptedContainerIds.delete(adoptedContainerId);
        serviceAdoptedContainer.delete(id);
      }
      services.delete(id);
      await delay(undefined, 250);
    },

    // ---- Adopt bind (ADR-0038 P3/P4b) ----
    // Binds an UNMANAGED container to a brand-new Service + Placement without touching
    // the workload (no pull/create/start/relabel) — mirrors `AdoptContainer` server-side
    // exactly, including its precondition order (node, then container, then the
    // already-adopted/name-collision refusals) and its status posture (the Service starts
    // `"created"` like any other; only the PLACEMENT is written straight to `"running"`,
    // since the container was already running).
    async adoptService(projectId: string, input: AdoptServiceInput): Promise<Service> {
      requireProject(projectId);
      requireNode(input.nodeId);
      const container = requireContainer(input.nodeId, input.containerId);

      if (adoptedContainerIds.has(container.id)) {
        throw new ApiError(
          "This container is already adopted — it's already bound to a service in this fleet. " +
            "Un-adopt that service first if you want to bind it somewhere else.",
          409,
          "already_adopted",
        );
      }
      // Mirrors AdoptContainer reusing CreateService's own name validation (the
      // candidate name is the container's own name) — an over-length or
      // otherwise-invalid container name is a clean 400, never a 500.
      if (!validResourceName(container.name)) {
        throw new ApiError(
          `Container name "${container.name}" doesn't fit a service name (1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit).`,
          400,
          "invalid_service",
        );
      }
      if (Array.from(services.values()).some((s) => s.projectId === projectId && s.name === container.name)) {
        throw new ApiError(
          `A service named "${container.name}" already exists in this project.`,
          409,
          "conflict",
        );
      }

      const spec = ADOPT_SPECS[container.id] ?? EMPTY_ADOPT_SPEC;
      const env: Record<string, string> = {};
      for (const key of spec.envKeys) env[key] = "";

      serviceSeq += 1;
      const now = new Date().toISOString();
      const created: Service = {
        id: `svc-mock-${serviceSeq}`,
        projectId,
        tenant: MOCK_TENANT,
        name: container.name,
        image: container.image,
        env,
        secretRefs: [],
        desiredCount: 1,
        status: "created",
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
        volumes: spec.volumes.length > 0 ? spec.volumes : undefined,
        command: spec.command.length > 0 ? spec.command : undefined,
        entrypoint: spec.entrypoint.length > 0 ? spec.entrypoint : undefined,
        restartPolicy: spec.restartPolicy.name ? spec.restartPolicy : undefined,
        healthcheck: (spec.healthcheck.test?.length ?? 0) > 0 ? spec.healthcheck : undefined,
      };
      services.set(created.id, created);

      placementSeq += 1;
      const placement: Placement = {
        id: `plc-mock-${placementSeq}`,
        serviceId: created.id,
        nodeId: input.nodeId,
        // Written directly to "running", bypassing the ordinary deploy path — the
        // container was already running, and adopt never pulls/creates/starts anything.
        status: "running",
        // The durable adopted signal (issue #330): the placement is bound to the existing
        // container by id, so `adopted` is true until a takeover clears containerId.
        containerId: container.id,
        adopted: true,
        envOverrides: {},
        coLocate: "",
        createdAt: now,
        updatedAt: now,
      };
      placements.set(placement.id, placement);

      adoptedContainerIds.add(container.id);
      serviceAdoptedContainer.set(created.id, container.id);

      return delay(clone(created), 400);
    },

    // The READ-ONLY pre-persist preview (issue #331): inspects the container and returns
    // exactly what adoptService WOULD capture, persisting NOTHING — no service, no
    // placement, no adopt bookkeeping. Mirrors PreviewAdoptContainer server-side, including
    // that it does NOT depend on the project (the authz is the role grant + node tenant,
    // not project existence) and never refuses an already-adopted container (only the
    // persisting adoptService does).
    async adoptPreview(_projectId: string, input: AdoptServiceInput): Promise<AdoptPreview> {
      requireNode(input.nodeId);
      const container = requireContainer(input.nodeId, input.containerId);
      const spec = ADOPT_SPECS[container.id] ?? EMPTY_ADOPT_SPEC;
      const env: Record<string, string> = {};
      for (const key of spec.envKeys) env[key] = "";
      const preview: AdoptPreview = {
        containerId: container.id,
        adopted: true,
        name: container.name,
        image: container.image,
        env,
        volumes: spec.volumes.length > 0 ? spec.volumes : undefined,
        command: spec.command.length > 0 ? spec.command : undefined,
        entrypoint: spec.entrypoint.length > 0 ? spec.entrypoint : undefined,
        restartPolicy: spec.restartPolicy.name ? spec.restartPolicy : undefined,
        healthcheck: (spec.healthcheck.test?.length ?? 0) > 0 ? spec.healthcheck : undefined,
      };
      return delay(clone(preview), 300);
    },

    // ---- Compose import (ADR-0025 §2, PR #121) ----
    // Mirrors Service.ImportCompose's own precondition order: project exists
    // and is EMPTY (ErrProjectNotEmpty -> 409, "merge" is a future feature)
    // before the document is even parsed, then one Service per compose
    // service (DesiredCount 1, matching defaultImportedDesiredCount
    // server-side) and one REQUIRED connection per depends_on edge. No
    // Placement is ever created — import populates the graph, the operator
    // places afterward.
    async importCompose(projectId: string, composeText: string): Promise<ComposeImportResult> {
      requireProject(projectId);
      const alreadyHasServices = Array.from(services.values()).some(
        (s) => s.projectId === projectId,
      );
      if (alreadyHasServices) {
        throw new ApiError(
          "This project already has services — compose import requires an empty project.",
          409,
          "project_not_empty",
        );
      }

      const parsed = parseComposeForMock(composeText);
      const byName = new Map<string, Service>();
      const createdServices: Service[] = [];
      for (const svc of parsed) {
        if (!validResourceName(svc.name)) {
          throw new ApiError(`compose: invalid service name "${svc.name}"`, 400, "invalid_compose");
        }
        if (byName.has(svc.name)) {
          throw new ApiError(`compose: duplicate service name "${svc.name}"`, 400, "invalid_compose");
        }
        serviceSeq += 1;
        const now = new Date().toISOString();
        const created: Service = {
          id: `svc-mock-${serviceSeq}`,
          projectId,
          tenant: MOCK_TENANT,
          name: svc.name,
          image: svc.image,
          env: {},
          secretRefs: [],
          desiredCount: 1,
          status: "created",
          createdBy: account.id,
          createdAt: now,
          updatedAt: now,
        };
        services.set(created.id, created);
        byName.set(svc.name, created);
        createdServices.push(created);
      }

      const createdConnections: Connection[] = [];
      const seenPairs = new Set<string>();
      for (const svc of parsed) {
        const from = byName.get(svc.name);
        if (!from) continue;
        for (const depName of svc.dependsOn) {
          const to = byName.get(depName);
          if (!to) {
            throw new ApiError(
              `compose: service "${svc.name}" depends_on unknown service "${depName}"`,
              400,
              "invalid_compose",
            );
          }
          const pairKey = `${from.id}->${to.id}`;
          if (seenPairs.has(pairKey)) continue; // repeated depends_on entry — dedup, mirrors the server
          seenPairs.add(pairKey);
          connectionSeq += 1;
          const created: Connection = {
            id: `conn-mock-${connectionSeq}`,
            fromService: from.id,
            toService: to.id,
            port: null,
            required: true,
            createdAt: new Date().toISOString(),
          };
          connections.set(created.id, created);
          createdConnections.push(created);
        }
      }

      return delay({ services: clone(createdServices), connections: clone(createdConnections) }, 400);
    },

    // Mirrors server/internal/projectsvc/deploy.go's TriggerDeploy (ADR-0025
    // §4): deploying a service deploys EVERY one of its placements, and the
    // trigger is ACCEPTED, not awaited — it returns a "deploying" Deployment
    // immediately, and the real outcome lands on the deployment/placement
    // rows a poller (useDeployService/useDeployments/usePlacements in
    // queries.ts) picks up later. This mock has no real background worker,
    // but it fakes the same shape with a timer, exactly like createInstance's
    // requested -> provisioning -> running sequence above: the returned
    // handle is captured (cloned) BEFORE the settle timer fires, so a caller
    // that inspects the return value alone sees "deploying," and only a
    // follow-up fetch (getService/listDeployments/listPlacements) sees the
    // settled state — never a fabricated synchronous success.
    async deployService(projectId: string, id: string): Promise<Deployment> {
      const service = requireService(projectId, id);
      if (service.desiredCount !== 1) {
        throw new ApiError(
          `desiredCount is ${service.desiredCount}; deploy currently supports exactly 1 replica.`,
          400,
          "invalid_desired_count",
        );
      }
      if (!service.image?.trim()) {
        throw new ApiError("This service has no image set; deploy needs an image.", 400, "invalid_service");
      }
      // ADR-0025 §2: a service runs wherever it has placements. One placed
      // nowhere has no deploy to perform — mirrors ErrNoPlacements, and (like
      // the real server) nothing is recorded for this refusal.
      const servicePlacements = Array.from(placements.values()).filter((p) => p.serviceId === id);
      if (servicePlacements.length === 0) {
        throw new ApiError(
          "This service has no placements; place it on a node first.",
          409,
          "no_placements",
        );
      }

      deploymentSeq += 1;
      const now = new Date().toISOString();
      const deployment: Deployment = {
        id: `dep-mock-${deploymentSeq}`,
        serviceId: id,
        tenant: MOCK_TENANT,
        status: "deploying",
        targetCount: service.desiredCount,
        // convergedCount stays 0 until the settle timer below writes a
        // per-placement outcome (A-5: derived from `deploymentPlacements`,
        // never a second hand-set number).
        convergedCount: 0,
        image: service.image,
        tag: imageTag(service.image),
        strategy: "rolling",
        maxUnavailable: 1,
        haltOnly: false,
        previousImage: currentRunningImage(id) || undefined,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      deployments.set(deployment.id, deployment);

      // Every placement marches to "deploying" right away — mirrors the
      // server writing that status on each row before it does any Docker
      // work, so a poll immediately after this call already shows progress
      // rather than a stale "pending"/"running".
      for (const p of servicePlacements) {
        placements.set(p.id, { ...p, status: "deploying", updatedAt: now });
      }

      // Deterministic failure hook for tests/demos: an image tagged with
      // "mock-deploy-fail" settles every placement (and the deployment, and
      // the service) as failed instead of running, so the failure path is
      // reachable without a real backend — never randomized, so a test can
      // rely on it.
      const shouldFail = service.image.includes("mock-deploy-fail");
      const settledPlacementStatus = shouldFail ? "failed" : "running";
      const settledDeploymentStatus = shouldFail ? "failed" : "running";
      const settledServiceStatus = shouldFail ? "error" : "running";

      setTimeout(() => {
        const settledAt = new Date().toISOString();
        const outcomes: DeploymentPlacementOutcome[] = [];
        for (const p of servicePlacements) {
          const current = placements.get(p.id);
          if (!current || current.status !== "deploying") continue; // deleted/moved meanwhile
          placements.set(p.id, { ...current, status: settledPlacementStatus, updatedAt: settledAt });
          outcomes.push(
            shouldFail
              ? { placementId: p.id, nodeId: p.nodeId, status: "failed", error: "container failed to become healthy" }
              : { placementId: p.id, nodeId: p.nodeId, status: "running" },
          );
        }
        const currentDep = deployments.get(deployment.id);
        if (currentDep && currentDep.status === "deploying") {
          deployments.set(deployment.id, {
            ...currentDep,
            status: settledDeploymentStatus,
            convergedCount: shouldFail ? 0 : outcomes.length,
            updatedAt: settledAt,
          });
          deploymentPlacements.set(deployment.id, outcomes);
        }
        const currentSvc = services.get(id);
        if (currentSvc) {
          services.set(id, { ...currentSvc, status: settledServiceStatus, updatedAt: settledAt });
        }
      }, 500);

      return delay(clone(deployment), 220);
    },

    async listDeployments(params: ListDeploymentsParams): Promise<Page<Deployment>> {
      requireService(params.projectId, params.serviceId);
      const scoped = Array.from(deployments.values())
        .filter((d) => d.serviceId === params.serviceId)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async getDeployment(projectId: string, serviceId: string, deploymentId: string): Promise<DeploymentDetail> {
      requireService(projectId, serviceId);
      const deployment = deployments.get(deploymentId);
      if (!deployment || deployment.serviceId !== serviceId) {
        throw new ApiError(`Deployment "${deploymentId}" was not found for this service.`, 404, "not_found");
      }
      // No entry means "no outcome summary yet" (an in-flight deploy) — an
      // honest empty breakdown, never fabricated (A-10, see the map's doc
      // comment in fixtures.ts).
      const outcomes = deploymentPlacements.get(deploymentId) ?? [];
      return delay(clone({ ...deployment, placements: outcomes }));
    },

    // Mirrors server/internal/projectsvc/deploy.go's Redeploy: re-applies a
    // past deployment's EXACT artifact (pinned to its recorded digest) through
    // the same rolling machinery as a forward deploy. UNLIKE rollback it stays
    // subject to the target environment's requires_promotion gate — see
    // `findGatingEnvironment`.
    async redeployDeployment(
      projectId: string,
      serviceId: string,
      deploymentId: string,
      options?: RedeployOptions,
    ): Promise<Deployment> {
      const service = requireService(projectId, serviceId);
      const target = requireDeploymentTarget(service, deploymentId, "redeploy");
      const gatingEnv = findGatingEnvironment(service.id);
      if (gatingEnv) {
        throw new ApiError(
          `The target environment ("${gatingEnv.name}") requires promotion; direct redeploys are refused there. Promote this service from its source environment instead.`,
          409,
          "requires_promotion",
        );
      }
      return triggerRedeployLikeAction(service, target, "you", options);
    },

    // Mirrors server/internal/projectsvc/deploy.go's Rollback: re-applies the
    // target deployment's recorded image/env. UNLIKE redeploy it is NOT
    // subject to requires_promotion (restoring a known-good state the
    // environment already ran is always allowed, ADR-0031 §2b).
    async rollbackDeployment(
      projectId: string,
      serviceId: string,
      deploymentId: string,
      options?: RedeployOptions,
    ): Promise<Deployment> {
      const service = requireService(projectId, serviceId);
      const target = requireDeploymentTarget(service, deploymentId, "rollback");
      return triggerRedeployLikeAction(service, target, "you", options);
    },

    // Read-only plan/volume-risk preview (ADR-0031 §2d) — mirrors
    // `projectsvc.PreviewDeploy`'s `ReadWriteNamedVolumes` filter exactly: a
    // service-declared VOLUME mount (never a host-path BIND — that's an S-10
    // escalation vector, not a rollback data-risk) that isn't read-only is
    // the data a rollback restores the IMAGE but not the CONTENTS of. Pure
    // read — touches no node, resolves no secret.
    async getDeployPreview(projectId: string, serviceId: string): Promise<DeployPreview> {
      const service = requireService(projectId, serviceId);
      const volumeRisk = Array.from(
        new Set(
          (service.volumes ?? [])
            .filter((v) => v.type === "volume" && v.mode !== "ro")
            .map((v) => v.source),
        ),
      ).sort();
      const servicePlacements = Array.from(placements.values()).filter((p) => p.serviceId === serviceId);
      return delay(
        clone({
          serviceId,
          strategy: "rolling",
          tenant: MOCK_TENANT,
          placements: servicePlacements.map((p) => ({
            placementId: p.id,
            nodeId: p.nodeId,
            strategy: "rolling",
            volumeRisk: volumeRisk.length > 0 ? volumeRisk : undefined,
            volumeWarning:
              volumeRisk.length > 0
                ? "this placement mounts a read-write named volume; a rollback restores the container image, not the volume's data."
                : undefined,
          })),
        }),
      );
    },

    // ---- The Placement Graph (ADR-0025): placements ----
    // Enforces the same UNIQUE(serviceId, nodeId) invariant the server does
    // (ADR-0025 §2) — this mock is the one place besides the real server
    // that constraint has to hold, so a demo/dev build behaves like
    // production rather than silently allowing a duplicate join.

    async listPlacements(params: ListPlacementsParams): Promise<Page<Placement>> {
      requireService(params.projectId, params.serviceId);
      const scoped = Array.from(placements.values())
        .filter((p) => p.serviceId === params.serviceId)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async createPlacement(
            projectId: string,
      serviceId: string,
      input: CreatePlacementInput,
    ): Promise<Placement> {
      requireService(projectId, serviceId);
      requireNode(input.nodeId);
      if (
        Array.from(placements.values()).some((p) => p.serviceId === serviceId && p.nodeId === input.nodeId)
      ) {
        throw new ApiError(
          `This service already has a placement on that node — one placement per service per node.`,
          409,
          "conflict",
        );
      }
      placementSeq += 1;
      const now = new Date().toISOString();
      const created: Placement = {
        id: `plc-mock-${placementSeq}`,
        serviceId,
        nodeId: input.nodeId,
        // Always starts pending — a real deploy (follow-up PR) is what
        // advances this, never the create call itself (S-15).
        status: "pending",
        // A deploy-owned placement is never adopted — no bound pre-existing container.
        adopted: false,
        envOverrides: input.envOverrides ?? {},
        coLocate: input.coLocate ?? "",
        createdAt: now,
        updatedAt: now,
      };
      placements.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updatePlacement(
            projectId: string,
      serviceId: string,
      placementId: string,
      input: UpdatePlacementInput,
    ): Promise<Placement> {
      const existing = requirePlacement(projectId, serviceId, placementId);
      const updated: Placement = {
        ...existing,
        envOverrides: input.envOverrides ?? existing.envOverrides,
        coLocate: input.coLocate ?? existing.coLocate,
        updatedAt: new Date().toISOString(),
      };
      placements.set(placementId, updated);
      return delay(clone(updated), 250);
    },

    async deletePlacement(projectId: string, serviceId: string, placementId: string): Promise<void> {
      requirePlacement(projectId, serviceId, placementId);
      placements.delete(placementId);
      await delay(undefined, 200);
    },

    async listNodePlacements(params: ListNodePlacementsParams): Promise<Page<Placement>> {
      requireNode(params.nodeId);
      const scoped = Array.from(placements.values())
        .filter((p) => p.nodeId === params.nodeId)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    // ---- The Placement Graph (ADR-0025): connections ----
    // Declared topology only (port/required do not enforce reachability yet
    // — ADR-0025 §2). Immutable once created: no updateConnection exists on
    // this contract, matching client.ts.

    async listConnections(params: ListConnectionsParams): Promise<Page<Connection>> {
      requireProject(params.projectId);
      const scoped = Array.from(connections.values())
        .filter((c) => connectionInScope(params.projectId, c))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async createConnection(projectId: string, input: CreateConnectionInput): Promise<Connection> {
      requireProject(projectId);
      const from = requireService(projectId, input.fromService);
      const to = requireService(projectId, input.toService);
      if (from.id === to.id) {
        throw new ApiError("A service can't connect to itself.", 400, "self_connection");
      }
      if (
        Array.from(connections.values()).some(
          (c) => c.fromService === from.id && c.toService === to.id && connectionInScope(projectId, c),
        )
      ) {
        throw new ApiError(
          `A connection from "${from.name}" to "${to.name}" already exists.`,
          409,
          "conflict",
        );
      }
      if (input.port !== undefined && (!Number.isInteger(input.port) || input.port < 1 || input.port > 65535)) {
        throw new ApiError("Port must be an integer between 1 and 65535.", 400, "invalid_connection");
      }
      connectionSeq += 1;
      const created: Connection = {
        id: `conn-mock-${connectionSeq}`,
        fromService: from.id,
        toService: to.id,
        port: input.port ?? null,
        required: input.required ?? false,
        createdAt: new Date().toISOString(),
      };
      connections.set(created.id, created);
      return delay(clone(created), 300);
    },

    async deleteConnection(projectId: string, connectionId: string): Promise<void> {
      requireConnection(projectId, connectionId);
      connections.delete(connectionId);
      await delay(undefined, 200);
    },

    // ADR-0029 P3-5 stub — a project-level BATCH route: one array covering
    // every connection in the project, not one call per connection. Derives
    // `crossNode` from the SAME placement data `requireNode`/`listPlacements`
    // already expose (never fabricated), and a believable `reachable` from
    // whether the target's node(s) are currently online — good enough to
    // exercise every reachability state in a UI test by overriding this
    // method with a bespoke array, the same pattern every other test in this
    // codebase uses for a bespoke response.
    async getConnectionStatuses(params: GetConnectionStatusesParams): Promise<ConnectionStatus[]> {
      requireProject(params.projectId);
      const scoped = Array.from(connections.values()).filter((c) =>
        connectionInScope(params.projectId, c),
      );

      const statuses: ConnectionStatus[] = scoped.map((connection) => {
        const fromNodeIds = new Set(
          Array.from(placements.values())
            .filter((p) => p.serviceId === connection.fromService)
            .map((p) => p.nodeId),
        );
        const toNodeIds = new Set(
          Array.from(placements.values())
            .filter((p) => p.serviceId === connection.toService)
            .map((p) => p.nodeId),
        );
        const sharesNode = Array.from(fromNodeIds).some((id) => toNodeIds.has(id));
        const crossNode = fromNodeIds.size > 0 && toNodeIds.size > 0 && !sharesNode;

        if (!crossNode) {
          return {
            connectionId: connection.id,
            crossNode: false,
            reachable: "yes",
            lastChecked: new Date().toISOString(),
            detail: "Same-node connection — served over the project bridge network, no gateway hop required.",
          };
        }

        const targetNodesOnline =
          toNodeIds.size > 0 && Array.from(toNodeIds).every((id) => nodes.get(id)?.status === "online");
        return {
          connectionId: connection.id,
          crossNode: true,
          reachable: targetNodesOnline ? "yes" : "no",
          lastChecked: new Date().toISOString(),
          detail: targetNodesOnline
            ? "Gateway dial healthy."
            : "Gateway dial failing — the target node is not currently connected.",
        };
      });

      return delay(statuses, 150);
    },

    // ---- Service domain bindings (ADR-0026 §3/§5) ----
    // Enforces the server's hostname uniqueness invariant GLOBALLY (not just
    // within this tenant/fleet) — Domain's doc comment in types.ts explains
    // why that scope is deliberate — and lowercases on write so
    // "App.Example.com" collides with "app.example.com" here exactly as it
    // does server-side (RFC 4343 case-insensitivity, canonicalized by the
    // server before the uniqueness check). Fixtured domains carry a node-reported
    // `status` (#303) the list surfaces; a freshly CREATED row leaves it unset —
    // "Awaiting status" — because no node has reported serving it yet, matching
    // the server's create response, which never fabricates a status.

    async listDomains(
            projectId: string,
      serviceId: string,
      params: PageParams = {},
    ): Promise<Page<Domain>> {
      requireService(projectId, serviceId);
      const scoped = Array.from(domains.values())
        .filter((d) => d.serviceId === serviceId)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async createDomain(
            projectId: string,
      serviceId: string,
      input: CreateDomainInput,
    ): Promise<Domain> {
      requireService(projectId, serviceId);
      const hostname = input.hostname.trim();
      if (!validHostnameShape(hostname)) {
        throw new ApiError(
          "That doesn't look like a valid hostname — use a real domain, e.g. app.example.com.",
          400,
          "invalid_hostname",
        );
      }
      if (
        !Number.isInteger(input.targetPort) ||
        input.targetPort < 1 ||
        input.targetPort > 65535
      ) {
        throw new ApiError("Target port must be an integer between 1 and 65535.", 400, "invalid_port");
      }
      const canonical = hostname.toLowerCase();
      if (Array.from(domains.values()).some((d) => d.hostname === canonical)) {
        throw new ApiError(`That hostname is already in use — "${canonical}" is bound to another service.`, 409, "conflict");
      }
      domainSeq += 1;
      const created: Domain = {
        id: `dom-mock-${domainSeq}`,
        serviceId,
        hostname: canonical,
        targetPort: input.targetPort,
        createdAt: new Date().toISOString(),
      };
      domains.set(created.id, created);
      return delay(clone(created), 300);
    },

    async deleteDomain(projectId: string, serviceId: string, domainId: string): Promise<void> {
      requireDomain(projectId, serviceId, domainId);
      domains.delete(domainId);
      await delay(undefined, 200);
    },

    // ---- Hero flow: provider credentials (write-only — S-13) ----

    async listProviderCredentials(params: ListProviderCredentialsParams): Promise<Page<ProviderCredentialSummary>> {
      const scoped = Array.from(providerCredentials.values())
        .filter((c) => c.tenant === MOCK_TENANT)
        .sort((a, b) => a.label.localeCompare(b.label));
      return delay(paginate(clone(scoped), params));
    },

    async createProviderCredential(
            input: CreateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      if (!input.label.trim()) {
        throw new ApiError("Label must not be empty.", 400, "invalid_provider_credential");
      }
      if (!input.token.trim()) {
        throw new ApiError("Token must not be empty.", 400, "invalid_provider_credential");
      }
      providerCredentialSeq += 1;
      const now = new Date().toISOString();
      // `input.token` is intentionally discarded after this point — the
      // mock doesn't even hold onto it, matching the write-only contract
      // (S-13) the real server implements (envelope-encrypt & store, never
      // echo back). Never logged either.
      const created: ProviderCredentialSummary = {
        id: `provcred-mock-${providerCredentialSeq}`,
        kind: input.kind,
        label: input.label.trim(),
        tenant: MOCK_TENANT,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      providerCredentials.set(created.id, created);
      return delay(clone(created), 300);
    },

    async rotateProviderCredential(
            id: string,
      input: RotateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      const existing = requireProviderCredential(id);
      if (!input.token.trim()) {
        throw new ApiError("Token must not be empty.", 400, "invalid_provider_credential");
      }
      const updated: ProviderCredentialSummary = { ...existing, updatedAt: new Date().toISOString() };
      providerCredentials.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteProviderCredential(id: string): Promise<void> {
      requireProviderCredential(id);
      if (Array.from(instances.values()).some((i) => i.providerCredId === id)) {
        throw new ApiError(
          "This credential provisioned at least one instance still on record. Remove those instances first.",
          409,
          "credential_in_use",
        );
      }
      providerCredentials.delete(id);
      await delay(undefined, 200);
    },

    // ---- First-class environments (ADR-0035 §1, M1 PR-E) ----
    // Mirrors server/internal/environmentsvc closely enough that the demo/dev
    // build behaves like production: name/class/node-type validation on write,
    // duplicate-name conflict, and the `protected` guard on update/delete.

    async listEnvironments(params: PageParams = {}): Promise<Page<Environment>> {
      const scoped = Array.from(environments.values()).sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getEnvironment(envId: string): Promise<Environment> {
      return delay(clone(requireEnvironment(envId)));
    },

    async createEnvironment(input: CreateEnvironmentInput): Promise<Environment> {
      const name = input.name.trim();
      if (!validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_environment",
        );
      }
      if (!VALID_ENVIRONMENT_CLASSES.includes(input.class)) {
        throw new ApiError(`Class "${input.class}" is not one of ${VALID_ENVIRONMENT_CLASSES.join(", ")}.`, 400, "invalid_environment");
      }
      const nodeTypes = validateEnvironmentNodeTypes(input.allowedNodeTypes);
      if (Array.from(environments.values()).some((e) => e.name === name)) {
        throw new ApiError(`An environment named "${name}" already exists.`, 409, "conflict");
      }
      environmentSeq += 1;
      const now = new Date().toISOString();
      const created: Environment = {
        id: `env-mock-${environmentSeq}`,
        name,
        class: input.class,
        requiresPromotion: input.requiresPromotion,
        allowedNodeTypes: nodeTypes,
        protected: input.protected,
        tenant: "fleet-prod",
        createdAt: now,
        updatedAt: now,
      };
      environments.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateEnvironment(envId: string, patch: UpdateEnvironmentInput): Promise<Environment> {
      const existing = requireEnvironment(envId);
      const next: Environment = { ...existing };

      if (patch.name !== undefined) {
        const name = patch.name.trim();
        if (!validResourceName(name)) {
          throw new ApiError(
            "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
            400,
            "invalid_environment",
          );
        }
        if (Array.from(environments.values()).some((e) => e.id !== envId && e.name === name)) {
          throw new ApiError(`An environment named "${name}" already exists.`, 409, "conflict");
        }
        next.name = name;
      }
      if (patch.class !== undefined) {
        if (!VALID_ENVIRONMENT_CLASSES.includes(patch.class)) {
          throw new ApiError(`Class "${patch.class}" is not one of ${VALID_ENVIRONMENT_CLASSES.join(", ")}.`, 400, "invalid_environment");
        }
        // PROTECTED GUARD (b): a protected environment's class is frozen.
        if (existing.protected && patch.class !== existing.class) {
          throw new ApiError(
            "Cannot change the class of a protected environment; unprotect it first.",
            409,
            "protected",
          );
        }
        next.class = patch.class;
      }
      if (patch.requiresPromotion !== undefined) next.requiresPromotion = patch.requiresPromotion;
      if (patch.allowedNodeTypes !== undefined) {
        next.allowedNodeTypes = validateEnvironmentNodeTypes(patch.allowedNodeTypes);
      }
      if (patch.protected !== undefined) {
        // PROTECTED GUARD (c): lowering the guard needs an explicit confirmation.
        if (existing.protected && !patch.protected && !patch.confirmUnprotect) {
          throw new ApiError(
            "Clearing `protected` needs an explicit confirmation.",
            409,
            "protected",
          );
        }
        next.protected = patch.protected;
      }
      next.updatedAt = new Date().toISOString();
      environments.set(envId, next);
      return delay(clone(next), 250);
    },

    async deleteEnvironment(envId: string): Promise<void> {
      const existing = requireEnvironment(envId);
      // PROTECTED GUARD (a): a protected environment refuses deletion.
      if (existing.protected) {
        throw new ApiError(
          "Cannot delete a protected environment; unprotect it first.",
          409,
          "protected",
        );
      }
      // Mirrors the store's still-in-use conflict: refuse while this is
      // another environment's promotion source.
      if (Array.from(environments.values()).some((e) => e.sourceEnvId === envId)) {
        throw new ApiError(
          "Cannot delete this environment while it is another environment's promotion source.",
          409,
          "conflict",
        );
      }
      environments.delete(envId);
      await delay(undefined, 250);
    },

    async promoteService(envId: string, body: PromoteServiceInput): Promise<Deployment> {
      const target = requireEnvironment(envId);
      const service = requireService(body.projectId, body.serviceId);
      // The source is the target's declared promotion chain (never sent by the
      // caller). No chain -> nothing to promote from (mirrors the server's 409).
      if (!target.sourceEnvId) {
        throw new ApiError(
          `"${target.name}" has no configured promotion source; set its source environment first.`,
          409,
          "no_promotion_source",
        );
      }
      deploymentSeq += 1;
      const now = new Date().toISOString();
      const deployment: Deployment = {
        id: `dep-mock-${deploymentSeq}`,
        serviceId: service.id,
        tenant: "fleet-prod",
        status: "deploying",
        targetCount: service.desiredCount,
        // This mock does not yet settle a promotion the way deployService/
        // rollback/redeploy do (it stays "deploying" — a pre-existing gap in
        // this stub, not something this change fixes) — convergedCount is
        // honestly 0 for as long as that holds.
        convergedCount: 0,
        image: service.image,
        tag: service.image ? imageTag(service.image) : undefined,
        haltOnly: false,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      deployments.set(deployment.id, deployment);
      return delay(clone(deployment), 250);
    },

    // ---- LLM provider credentials (write-only key material — S-13/S-14) ----

    async listLlmCredentials(params: ListLlmCredentialsParams): Promise<Page<LlmCredentialSummary>> {
      const scoped = Array.from(llmCredentials.values())
        .filter((c) => c.tenant === MOCK_TENANT)
        .sort((a, b) => a.label.localeCompare(b.label));
      return delay(paginate(clone(scoped), params));
    },

    async createLlmCredential(
            input: CreateLlmCredentialInput,
    ): Promise<LlmCredentialSummary> {
      if (!input.label.trim()) {
        throw new ApiError("Label must not be empty.", 400, "invalid_llm_credential");
      }
      // Ollama is a keyless local endpoint; every other provider needs a key.
      if (input.provider !== "ollama" && !input.key.trim()) {
        throw new ApiError("API key must not be empty.", 400, "invalid_llm_credential");
      }
      llmCredentialSeq += 1;
      const now = new Date().toISOString();
      const created: LlmCredentialSummary = {
        id: `llm-mock-${llmCredentialSeq}`,
        provider: input.provider,
        baseUrl: input.baseUrl,
        label: input.label.trim(),
        tenant: MOCK_TENANT,
        models: input.models,
        budgetMonthlyUsd: input.budgetMonthlyUsd,
        createdBy: "admin",
        createdAt: now,
        updatedAt: now,
      };
      llmCredentials.set(created.id, created);
      return delay(clone(created), 300);
    },

    async rotateLlmCredentialKey(
            id: string,
      input: RotateLlmCredentialKeyInput,
    ): Promise<LlmCredentialSummary> {
      const existing = requireLlmCredential(id);
      if (!input.key.trim()) {
        throw new ApiError("API key must not be empty.", 400, "invalid_llm_credential");
      }
      const updated: LlmCredentialSummary = { ...existing, updatedAt: new Date().toISOString() };
      llmCredentials.set(id, updated);
      return delay(clone(updated), 250);
    },

    async updateLlmCredentialConfig(
            id: string,
      input: UpdateLlmCredentialConfigInput,
    ): Promise<LlmCredentialSummary> {
      const existing = requireLlmCredential(id);
      const updated: LlmCredentialSummary = {
        ...existing,
        models: input.models ?? existing.models,
        budgetMonthlyUsd: input.budgetMonthlyUsd ?? existing.budgetMonthlyUsd,
        updatedAt: new Date().toISOString(),
      };
      llmCredentials.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteLlmCredential(id: string): Promise<void> {
      requireLlmCredential(id);
      llmCredentials.delete(id);
      await delay(undefined, 200);
    },

    // ---- Agentic workbench sessions ----

    async listAgentSessions(params: ListAgentSessionsParams): Promise<Page<AgentSession>> {
      const scoped = Array.from(agentSessions.values())
        .filter((s) => s.tenant === MOCK_TENANT)
        .filter((s) => (params.local ? s.local === true : true))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async getAgentSession(id: string): Promise<AgentSession> {
      return delay(clone(requireAgentSession(id)), 150);
    },

    async createAgentSession(
            input: CreateAgentSessionInput,
    ): Promise<AgentSession> {
      if (!input.label.trim()) {
        throw new ApiError("Label must not be empty.", 400, "invalid_agent_session");
      }
      if (input.develop) {
        // The default mock behavior is the happy path (a develop image IS
        // configured) — a test exercising the founder-gate refusal
        // (`agentsession.Service.CreateDevelopSession`'s
        // `developImage == ""` branch) overrides `createAgentSession`
        // directly, the same per-call override pattern every other mock
        // refusal scenario in this codebase's tests already uses (see e.g.
        // BonnieSessionsPage.test.tsx's 422/409 cases).
        if (!input.projectId?.trim()) {
          throw new ApiError("Project is required to start a develop session.", 400, "invalid_agent_session");
        }
        requireProject(input.projectId);
      } else if (!input.image.trim()) {
        throw new ApiError("Image must not be empty.", 400, "invalid_agent_session");
      }
      if (!input.credentialId) {
        throw new ApiError("Credential is required.", 400, "invalid_agent_session");
      }
      requireLlmCredential(input.credentialId);
      agentSessionSeq += 1;
      const now = new Date().toISOString();
      const id = `asn_mock-${agentSessionSeq}`;
      // The stored row never carries the token — mirrors the real server
      // (S-13): only THIS call's response gets it (added below, after the
      // clone that becomes the persisted row), so a later listAgentSessions
      // or getAgentSession can never leak it. A develop session's
      // `projectId` IS stored and echoed back on every read (`AgentSession.
      // projectId`, IA slice 3) — see that field's doc comment in types.ts.
      const created: AgentSession = {
        id,
        tenant: MOCK_TENANT,
        credentialId: input.credentialId,
        status: "pending",
        image: input.develop ? "" : input.image.trim(),
        label: input.label.trim(),
        config: input.config,
        createdBy: "admin",
        createdAt: now,
        updatedAt: now,
        ...(input.local || input.develop
          ? {
              local: true,
              branch: input.branch?.trim() || `bonnie/${id}`,
              dirty: false,
              node: "my-laptop",
            }
          : {}),
        ...(input.develop && input.projectId ? { projectId: input.projectId } : {}),
      };
      agentSessions.set(id, created);
      // Simulate Kelpie/Bonnie picking up the session after a short delay.
      setTimeout(() => {
        const s = agentSessions.get(id);
        if (s && s.status === "pending") {
          agentSessions.set(id, {
            ...s,
            status: "running",
            nodeId: "node-web-1",
            containerId: `ctr-wb-${id}`,
            updatedAt: new Date().toISOString(),
          });
        }
      }, 1500);
      // A believable-looking opaque token — never a real credential, and
      // never logged or persisted anywhere by this mock (see comment above).
      const token = `wbtok_mock_${id}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      return delay({ ...clone(created), token }, 300);
    },

    async stopAgentSession(id: string): Promise<AgentSession> {
      const existing = requireAgentSession(id);
      if (existing.status === "stopped") {
        return delay(clone(existing), 200);
      }
      const updated: AgentSession = {
        ...existing,
        status: "stopped",
        stoppedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      agentSessions.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteAgentSession(id: string): Promise<void> {
      requireAgentSession(id);
      agentSessions.delete(id);
      await delay(undefined, 200);
    },

    async runAgentSessionTask(id: string, input: RunSessionTaskInput): Promise<void> {
      const session = requireAgentSession(id);
      if (!input.prompt.trim()) {
        throw new ApiError("Task prompt must not be empty.", 400, "invalid_task");
      }
      if (session.status !== "running") {
        throw new ApiError(
          `Session "${session.label}" isn't running — start it before sending a task.`,
          409,
          "session_not_running",
        );
      }
      // Simulate the agent picking up the task and touching the worktree —
      // mirrors the daemon's own async "queued, not yet done" contract (this
      // call returns before the agent has done anything).
      setTimeout(() => {
        const s = agentSessions.get(id);
        if (s && s.status === "running") {
          agentSessions.set(id, {
            ...s,
            dirty: true,
            changeSummary: "1 file changed, 12 insertions(+)",
            updatedAt: new Date().toISOString(),
          });
        }
      }, 1200);
      await delay(undefined, 250);
    },

    async promoteAgentSession(id: string, input: PromoteSessionInput): Promise<PromoteSessionResult> {
      const session = requireAgentSession(id);
      // Mirrors proto.BonniePromoteRequest.Validate: a non-Bonnie (no
      // worktree) session has nothing to promote.
      if (!session.branch) {
        throw new ApiError(
          `Session "${session.label}" has no worktree to promote — only Bonnie sessions can be promoted.`,
          422,
          "no_worktree",
        );
      }
      if (input.openPR && !input.prTitle?.trim()) {
        throw new ApiError("A pull request title is required to open a pull request.", 422, "pr_title_required");
      }
      const result: PromoteSessionResult = {
        branch: session.branch,
        pushed: true,
        prUrl: input.openPR
          ? `https://github.com/mock-org/mock-repo/pull/${1000 + agentSessionSeq}`
          : undefined,
      };
      agentSessions.set(id, {
        ...session,
        dirty: false,
        changeSummary: undefined,
        updatedAt: new Date().toISOString(),
      });
      return delay(result, 350);
    },

    async getProviderCatalog(credentialId: string): Promise<ProviderCatalog> {
      requireProviderCredential(credentialId);
      return delay(clone(MOCK_PROVIDER_CATALOG), 300);
    },

    // ---- Hero flow: instances (Hetzner provisioning) ----

    async listInstances(params: ListInstancesParams): Promise<Page<Instance>> {
      const scoped = Array.from(instances.values())
        .filter((i) => i.tenant === MOCK_TENANT && (!params.projectId || i.projectId === params.projectId))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async getInstance(id: string): Promise<Instance> {
      return delay(clone(requireInstance(id)));
    },

    async createInstance(input: CreateInstanceInput): Promise<Instance> {
      const credential = requireProviderCredential(input.providerCredId);
      if (input.projectId) requireProject(input.projectId);
      if (!input.region.trim() || !input.serverType.trim()) {
        throw new ApiError("Select a region and server type.", 400, "invalid_instance");
      }
      instanceSeq += 1;
      const now = new Date().toISOString();
      // Reserved BEFORE the provider call, mirroring `Instance.agentId`'s doc
      // comment (types.ts) exactly — this is the host's FUTURE enrollment
      // identity, not proof anything has enrolled yet. Setting it here (the
      // mock previously left it unset) is what lets a caller navigate
      // straight to the new host's progress view at `/hosts/{agentId}` the
      // instant provisioning is accepted, same as the real server.
      const agentId = `node-mock-${instanceSeq}`;
      const created: Instance = {
        id: `inst-mock-${instanceSeq}`,
        tenant: MOCK_TENANT,
        projectId: input.projectId,
        providerCredId: credential.id,
        providerKind: credential.kind,
        externalId: "",
        region: input.region.trim(),
        serverType: input.serverType.trim(),
        status: "requested",
        publicIpv4: "",
        agentId,
        createdBy: account.id,
        createdAt: now,
        updatedAt: now,
      };
      instances.set(created.id, created);

      // Fakes the requested -> provisioning -> running -> agent-enrolled arc
      // (real provisioning takes tens of seconds and a real Kelpie install
      // takes longer still; the Host UI polls/refetches while non-terminal —
      // see useHost). The final stage simulates Kelpie's first check-in by
      // enrolling the reserved node, exactly what turns an "enrolling" Host
      // into "online" server-side (hostsvc.deriveState) — without it, every
      // demo provision would strand at "enrolling" forever and understate
      // what the real product actually does (Host/Deploy UX Phase 3).
      setTimeout(() => {
        const current = instances.get(created.id);
        if (!current || current.status !== "requested") return;
        instances.set(created.id, {
          ...current,
          status: "provisioning",
          externalId: `hetzner-mock-${instanceSeq}`,
          updatedAt: new Date().toISOString(),
        });
      }, provisionStageDelaysMs.provisioning);
      setTimeout(() => {
        const current = instances.get(created.id);
        if (!current || current.status !== "provisioning") return;
        instances.set(created.id, {
          ...current,
          status: "running",
          publicIpv4: `203.0.113.${20 + (instanceSeq % 200)}`,
          updatedAt: new Date().toISOString(),
        });
      }, provisionStageDelaysMs.running);
      setTimeout(() => {
        const current = instances.get(created.id);
        if (!current || current.status !== "running" || nodes.has(agentId)) return;
        nodes.set(agentId, {
          id: agentId,
          name: `${current.serverType}-${instanceSeq}`,
          hostname: `${current.serverType}-${instanceSeq}.mock`,
          status: "online",
          agentVersion: "0.4.2",
          tenant: MOCK_TENANT,
          containerCount: 0,
          lastSeenAt: new Date().toISOString(),
          tags: [],
          connectAddress: "",
          environmentId: "",
          environmentName: "",
          nodeType: "standard",
        });
      }, provisionStageDelaysMs.enrolled);

      return delay(clone(created), 300);
    },

    async deleteInstance(id: string): Promise<void> {
      requireInstance(id);
      instances.delete(id);
      await delay(undefined, 250);
    },

    // ---- Org import: "bring your existing infra" (ADR-0048 §2, #472) ----

    async proposeImport(params: ProposeImportParams): Promise<ImportProposal> {
      // Mirrors provisioning.deferredImportReasons exactly (server/internal/
      // provisioning/import.go) — GitHub and Docker fail closed BEFORE any
      // credential is resolved, same posture as the real server (A-10: no
      // inert stub pretending to read a source that isn't in the tree).
      const deferredReason = DEFERRED_IMPORT_REASONS[params.provider];
      if (deferredReason) {
        throw new ApiError(deferredReason, 501, "not_implemented");
      }
      if (!params.credentialId) {
        throw new ApiError(`A credential id is required to import from "${params.provider}".`, 400, "invalid_credential");
      }
      const credential = requireProviderCredential(params.credentialId);
      // Only the demo Hetzner credential carries a canned proposal (mirroring
      // orgmap.Infer's real shape/diversity); every other real, non-deferred
      // provider kind honestly has nothing recorded yet rather than a faked
      // guess (S-15).
      const proposal: ImportProposal =
        credential.kind === "hetzner" ? clone(IMPORT_PROPOSAL_HETZNER) : { tenant: MOCK_TENANT, items: [] };
      return delay(proposal, 400);
    },

    async applyImport(items: ProposedImportItem[]): Promise<ApplyImportResult> {
      // Recomputes committability from the item's own fields rather than trusting the
      // wire `committable` flag, mirroring the real server's advisory-gate posture
      // (fromProposedItemJSON's doc comment, provisioning/http.go) — this mock is not
      // the security boundary either way (there's no cross-tenant surface to defend in
      // a single-tenant mock), but the BEHAVIOR under test — which items actually
      // create a project — should match production.
      let projectsCreated = 0;
      let projectsExisted = 0;
      let skipped = 0;
      for (const it of items) {
        if (!isImportItemCommittable(it)) {
          skipped += 1;
          continue;
        }
        const name = it.name.trim();
        const existing = Array.from(projects.values()).find((p) => p.tenant === MOCK_TENANT && p.name === name);
        if (existing) {
          projectsExisted += 1;
          continue;
        }
        projectSeq += 1;
        const now = new Date().toISOString();
        projects.set(`proj-mock-${projectSeq}`, {
          id: `proj-mock-${projectSeq}`,
          name,
          tenant: MOCK_TENANT,
          createdBy: account.id,
          createdAt: now,
          updatedAt: now,
        });
        projectsCreated += 1;
      }
      return delay({ projectsCreated, projectsExisted, skipped }, 350);
    },

    // ---- Billing (L-1) ----
    // The mock never simulates a Stripe redirect: `startCheckout`/`openBillingPortal`
    // reject, because handing the demo a fake payment URL to navigate to would be
    // worse than an honest "not available here". The read is enough to render the
    // page coherently for a billing_admin.

    async getSubscription(): Promise<Subscription> {
      return delay(clone(subscription));
    },

    async startCheckout(): Promise<string> {
      throw new ApiError("Checkout is not available in the demo.", 501, "not_implemented");
    },

    async openBillingPortal(): Promise<string> {
      throw new ApiError("The billing portal is not available in the demo.", 501, "not_implemented");
    },

    // ---- Licensing / entitlement (ADR-0017) ----
    // Fixed at "Free, no license, at the node ceiling" (see `entitlement`
    // above) rather than varied by a scenario switch — enough on its own to
    // demonstrate the near/at-limit affordances on the Nodes and Billing
    // pages. `createEnrollToken` above never simulates the 402 itself
    // (minting a token doesn't add a node in this mock, matching the real
    // guard's semantics); that path is covered by unit tests against a fake
    // `ApiClient`, not this demo client.
    async getEntitlement(): Promise<EntitlementStatus> {
      return delay(clone(entitlement));
    },

    // ---- Fleet health matrix (ADR-0030 P4-2, read-only) ----

    async listHealthMatrix(params: ListHealthParams): Promise<Page<HealthCell>> {
      const cells = computeHealthCells();
      return delay(paginate(clone(cells), params));
    },

    // ---- Fleet-health alerting (ADR-0030 P4-5) ----

    async listAlertRules(params: ListAlertRulesParams): Promise<Page<AlertRule>> {
      const scoped = Array.from(alertRules.values())
        .filter((r) => alertRuleFleet.get(r.id) === MOCK_TENANT)
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async createAlertRule(input: CreateAlertRuleInput): Promise<AlertRule> {
      if (!input.name.trim()) {
        throw new ApiError("Name must not be empty.", 400, "invalid_alert_rule");
      }
      if (!VALID_ALERT_CONDITIONS.includes(input.condition)) {
        throw new ApiError("Unsupported condition.", 400, "invalid_alert_rule");
      }
      if (input.webhookUrl && !input.webhookUrl.startsWith("https://")) {
        throw new ApiError("Webhook URL must be https.", 400, "invalid_alert_rule");
      }
      alertRuleSeq += 1;
      const now = new Date().toISOString();
      // `input.signingSecret` is intentionally discarded after this point —
      // the mock doesn't even hold onto it, matching the real service's
      // write-only contract (S-13/S-14).
      const created: AlertRule = {
        id: `alr-mock-${alertRuleSeq}`,
        name: input.name.trim(),
        condition: input.condition,
        threshold: input.threshold,
        scope: input.scope || undefined,
        webhookUrl: input.webhookUrl ?? "",
        enabled: input.enabled,
        createdAt: now,
        updatedAt: now,
      };
      alertRules.set(created.id, created);
      alertRuleFleet.set(created.id, MOCK_TENANT);
      alertInstances.set(created.id, []);
      return delay(clone(created), 300);
    },

    async updateAlertRule(
            id: string,
      input: UpdateAlertRuleInput,
    ): Promise<AlertRule> {
      const existing = requireAlertRule(id);
      if (input.name !== undefined && !input.name.trim()) {
        throw new ApiError("Name must not be empty.", 400, "invalid_alert_rule");
      }
      if (input.webhookUrl && !input.webhookUrl.startsWith("https://")) {
        throw new ApiError("Webhook URL must be https.", 400, "invalid_alert_rule");
      }
      // `input.signingSecret`, if present, is discarded here too — see
      // createAlertRule's note. Its presence/absence only matters to the
      // real server's "leave unchanged vs. clear" contract (client.ts); this
      // mock never stores a secret to begin with.
      const updated: AlertRule = {
        ...existing,
        name: input.name !== undefined ? input.name.trim() : existing.name,
        threshold: input.threshold !== undefined ? input.threshold : existing.threshold,
        scope: input.scope !== undefined ? input.scope || undefined : existing.scope,
        webhookUrl: input.webhookUrl !== undefined ? input.webhookUrl : existing.webhookUrl,
        enabled: input.enabled !== undefined ? input.enabled : existing.enabled,
        updatedAt: new Date().toISOString(),
      };
      alertRules.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteAlertRule(id: string): Promise<void> {
      requireAlertRule(id);
      alertRules.delete(id);
      alertRuleFleet.delete(id);
      alertInstances.delete(id);
      await delay(undefined, 200);
    },

    async listAlertInstances(params: ListAlertInstancesParams): Promise<Page<AlertInstance>> {
      requireAlertRule(params.ruleId);
      const scoped = (alertInstances.get(params.ruleId) ?? [])
        .slice()
        .sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt));
      return delay(paginate(clone(scoped), params));
    },
  };
}
